import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import agenticApi from '../utils/agenticApi';
import styled, { keyframes, css } from 'styled-components';

// ─── Data ──────────────────────────────────────────────────────────────────
const PIPELINE_STAGES = [
  { id: "req", label: "Requirements", icon: "📋", agent: "Requirements Agent", status: "complete", time: "2m 14s" },
  { id: "design", label: "Architecture", icon: "🏗️", agent: "Design Agent", status: "complete", time: "4m 38s" },
  { id: "code", label: "Coding", icon: "⚡", agent: "Coding Agent", status: "active", time: "Running…" },
  { id: "qa", label: "QA", icon: "🛡️", agent: "QA Agent", status: "pending", time: "Queued" },
  { id: "deploy", label: "Deploy", icon: "🚀", agent: "Deploy Agent", status: "pending", time: "Queued" },
];

const SUPERVISORS = [
  { name: "Aisha K.", role: "Req. Analyst", color: "#7B2FFF", status: "complete", initial: "AK" },
  { name: "Marco R.", role: "Architect", color: "#00C2FF", status: "complete", initial: "MR" },
  { name: "Priya S.", role: "Sr. Developer", color: "#10B981", status: "active", initial: "PS" },
  { name: "Jin L.", role: "QA Lead", color: "#64748B", status: "pending", initial: "JL" },
  { name: "Omar T.", role: "DevOps Eng.", color: "#64748B", status: "pending", initial: "OT" },
];

// const FEATURES = [ ... ]; // Removed as they are now live or unused

// const ACTIVITY = [ ... ]; // Removed as they are now live

const SIDEBAR_REVIEWS = [
  { title: "2FA Implementation PR", client: "Acme Corp", phase: "Code Review", urgency: "NOW", dot: "#00C2FF", urgencyColor: "rgba(239,68,68,0.15)", urgencyText: "#EF4444" },
  { title: "Analytics Dashboard — Spec", client: "DataSpark", phase: "Requirements", urgency: "30m", dot: "#7B2FFF", urgencyColor: "rgba(245,158,11,0.12)", urgencyText: "#F59E0B" },
  { title: "Notification System — QA", client: "NovaTech", phase: "QA Report", urgency: "45m", dot: "#F59E0B", urgencyColor: "rgba(245,158,11,0.12)", urgencyText: "#F59E0B" },
  { title: "API Rate Limiter — Design", client: "Acme Corp", phase: "Architecture", urgency: "2h", dot: "#00C2FF", urgencyColor: "rgba(100,116,139,0.12)", urgencyText: "#64748B" },
  { title: "OAuth SSO Integration", client: "FinFlow", phase: "Code Review", urgency: "3h", dot: "#10B981", urgencyColor: "rgba(100,116,139,0.12)", urgencyText: "#64748B" },
];

const CODE_LINES = [
  { type: "comment", text: "// Generated by Globl.ai Coding Agent · Session #a7f2c · Revision 1" },
  { type: "normal", text: "" },
  { type: "keyword", text: "import ", suffix: "{ authenticator } ", suf2: "from ", suf3: "'@app/auth';" },
  { type: "keyword", text: "import ", suffix: "{ rateLimit } ", suf2: "from ", suf3: "'@app/middleware';" },
  { type: "normal", text: "" },
  { type: "add", text: "+ export async function generateTOTP(userId: string): Promise<TOTPResult> {" },
  { type: "add", text: "+   const secret = authenticator.generateSecret();" },
  { type: "add", text: "+   const uri = authenticator.keyuri(userId, 'Globl.ai', secret);" },
  { type: "add", text: "+   const qr = await QRCode.toDataURL(uri);" },
  { type: "add", text: "+   await db.userSecrets.upsert({ userId, secret, enabled: false });" },
  { type: "add", text: "+   return { secret, qr, backupCodes: generateBackupCodes() };" },
  { type: "add", text: "+ }" },
  { type: "normal", text: "" },
  { type: "add", text: "+ export async function verifyTOTP(userId: string, token: string) {" },
  { type: "add", text: "+   const { secret } = await db.userSecrets.findOrThrow({ userId });" },
  { type: "add", text: "+   const valid = authenticator.check(token, secret);" },
  { type: "add", text: "+   if (!valid) throw new AuthError('Invalid TOTP token');" },
  { type: "add", text: "+   return db.sessions.markMFAComplete(userId);" },
  { type: "add", text: "+ }" },
];

const CONF_BARS = [
  { label: "Business Logic", val: 94, color: "#10B981" },
  { label: "Test Coverage", val: 87, color: "#00C2FF" },
  { label: "Security Patterns", val: 91, color: "#7B2FFF" },
  { label: "Code Style", val: 98, color: "#10B981" },
  { label: "Edge Cases", val: 72, color: "#F59E0B" },
];

const REVIEWER_ROLES = ['Senior Engineer', 'Architect', 'QA Lead', 'Product Manager', 'Tech Lead', 'DevOps Engineer'];

// ─── Animations ─────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const pulseCyan = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,194,255,0.4); }
  50%       { box-shadow: 0 0 0 8px rgba(0,194,255,0); }
`;
const blink = keyframes`
  0%, 100% { opacity: 1; } 50% { opacity: 0.3; }
`;
const fadeInDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: ${props => props.size || '14px'};
  height: ${props => props.size || '14px'};
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  border-top-color: currentColor;
  animation: ${spin} 0.6s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
`;

// ─── Global Styled Components ────────────────────────────────────────────────
const WorkflowWrapper = styled.div`
  background: #060D1A;
  color: #FFFFFF;
  font-family: 'Outfit', sans-serif;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  
  // Responsive sidebar adjustments
  margin-left: ${props => props.expanded ? '0' : (props.collapsed ? '60px' : '240px')};
  width: ${props => props.expanded ? '100%' : (props.collapsed ? 'calc(100% - 60px)' : 'calc(100% - 240px)')};
  transition: all 0.3s ease;

  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Outfit:wght@300;400;500;600;700&display=swap');

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: #0A1628; }
  &::-webkit-scrollbar-thumb { background: #1A3550; border-radius: 2px; }

  @media screen and (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const Page = styled.div`
  padding-bottom: 90px;
  // padding-top: 64px; // MainContentArea in App.js already adds 64px margin-top
`;

const SectionTitle = styled.div`
  font-family: 'Cinzel', serif;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #FFFFFF, rgba(255, 255, 255, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSub = styled.div`
  color: #94A3B8;
  font-size: 15px;
  margin-bottom: 40px;
`;

const FadeInDiv = styled.div`
  animation: ${props => props.delay ? css`${fadeUp} 0.6s ${props.delay} ease both` : css`${fadeUp} 0.6s ease both`};
`;

const BlinkingDot = styled.div`
  width: 6px;
  height: 6px;
  borderRadius: 50%;
  background: #10B981;
  animation: ${css`${blink} 1.5s infinite`};
`;

const ScreenNavContainer = styled.div`
  position: fixed;
  bottom: 24px;
  left: ${props => props.expanded ? '50%' : (props.collapsed ? 'calc(50% + 30px)' : 'calc(50% + 120px)')};
  transform: translateX(-50%);
  background: rgba(10, 22, 40, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 40px;
  padding: 6px;
  display: flex;
  gap: 4px;
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;

  @media screen and (max-width: 768px) {
    left: 50%;
  }
`;
const NotificationBanner = styled.div`
  position: absolute; top: 20px; left: 36px; right: 36px; z-index: 10;
  background: ${props => props.type === 'approve' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'};
  border: 1px solid ${props => props.type === 'approve' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'};
  border-radius: 10px; padding: 12px 20px; display: flex; align-items: center; gap: 12px;
  animation: ${fadeInDown} 0.4s ease;
`;

// ─── Workflow Stepper Component ──────────────────────────────────────────────
const WorkflowStepper = ({ steps, currentStepIdx, onNext, onBack, nextLabel, backLabel }) => {
  return (
    <div style={{ marginBottom: 32, background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ display: 'flex', flex: 1 }}>
          {steps.map((step, idx) => {
            const isCompleted = idx < currentStepIdx;
            const isActive = idx === currentStepIdx;
            return (
              <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: isCompleted ? '#10B981' : isActive ? 'linear-gradient(135deg, #00C2FF, #7B2FFF)' : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: isActive || isCompleted ? '#fff' : '#94A3B8', zIndex: 2 }}>
                    {isCompleted ? '✓' : idx + 1}
                  </div>
                  {idx < steps.length - 1 && (
                    <div style={{ flex: 1, height: 2, background: isCompleted ? '#10B981' : 'rgba(255,255,255,0.1)', margin: '0 12px' }} />
                  )}
                </div>
                <div style={{ marginTop: 12, paddingRight: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: isActive ? '#00C2FF' : isCompleted ? '#E2E8F0' : '#64748B' }}>{step.title}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4, maxWidth: '90%' }}>{step.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: 'rgba(0,194,255,0.05)', border: '1px solid rgba(0,194,255,0.2)', borderRadius: 12, padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ fontSize: 24 }}>💡</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#00C2FF', marginBottom: 4 }}>Screen Tutor</div>
            <div style={{ fontSize: 13, color: '#E2E8F0', lineHeight: 1.5 }}>
              {steps[currentStepIdx]?.tutor || "Follow the on-screen instructions."}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {onBack && (
            <button onClick={onBack} style={{ padding: '10px 20px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, cursor: 'pointer' }}>
              {backLabel || 'Back'}
            </button>
          )}
          {onNext && (
            <button onClick={onNext} style={{ padding: '10px 20px', borderRadius: 8, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              {nextLabel || 'Next Step →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Pipeline Component ──────────────────────────────────────────────────────
const PipelineContainer = styled.div`
  background: #0A1628;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: ${props => props.compact ? '24px' : '40px'};
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

// (Shared pipeline styles duplicated here or made global)
const StageWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;
const StageCard = styled.div`
  flex: 1;
  background: #0F2040;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  position: relative;
  transition: all 0.3s;
  cursor: pointer;
  &.complete { border-color: rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.04); }
  &.active  { border-color: #00C2FF; background: rgba(0, 194, 255, 0.06); animation: ${css`${pulseCyan} 2s infinite`}; }
  &.pending { opacity: 0.5; }
`;

function PipelineViz({ compact = false, workflowId = null }) {
  const [overview, setOverview] = useState(null);
  const [fallbackActive, setFallbackActive] = useState(2);

  useEffect(() => {
    if (!workflowId) return;
    const fetchOverview = async () => {
      try {
        const res = await agenticApi.getWorkflowOverview(workflowId);
        setOverview(res);
      } catch (e) {
        console.error("Pipeline poll failed", e);
      }
    };
    fetchOverview();
    const timer = setInterval(fetchOverview, 5000);
    return () => clearInterval(timer);
  }, [workflowId]);

  const liveStages = overview?.active_stage?.template_stages?.map((name, i) => {
    const activeIdx = overview.active_stage.stage_index;
    return {
      id: name,
      label: name,
      icon: i === 0 ? "📋" : i === 1 ? "🏗️" : i === 2 ? "⚡" : i === 3 ? "🛡️" : "🚀",
      agent: `${name} Agent`,
      status: i < activeIdx ? 'complete' : i === activeIdx ? 'active' : 'pending'
    };
  });

  const displayStages = liveStages || PIPELINE_STAGES;
  const activeIdx = overview?.active_stage?.stage_index ?? fallbackActive;

  return (
    <PipelineContainer compact={compact}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 40 }}>
        <div>
          <div style={{ fontFamily: 'DM Mono', color: '#00C2FF', fontSize: 12, letterSpacing: '0.1em' }}>SDLC EXECUTION PIPELINE</div>
          <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#64748B', marginTop: 4 }}>
            {overview ? (
              `${overview.workflow?.workflow_name} · ${overview.active_stage?.stage_name} is ${overview.active_stage?.status}`
            ) : (
              "Feature: Two-Factor Authentication · Acme Corp · Started 38 min ago"
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10B981', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', padding: '4px 10px', borderRadius: 20, fontSize: 11 }}>
          <BlinkingDot /> LIVE
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {displayStages.map((s, i) => (
          <StageWrapper key={i}>
            <StageCard
              className={s.status}
              onClick={() => !workflowId && setFallbackActive(i)}
              style={{ outline: activeIdx === i ? "2px solid rgba(0,194,255,0.4)" : "none", outlineOffset: 3 }}
            >
              <div style={{ fontSize: 24, marginBottom: 10 }}>{s.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, color: s.status === 'complete' ? '#10B981' : s.status === 'active' ? '#00C2FF' : '#64748B' }}>{s.label}</div>
              <div style={{ fontFamily: 'DM Mono', fontSize: 10, color: '#94A3B8' }}>{s.agent}</div>
              <div style={{ marginTop: 10, fontFamily: 'DM Mono', fontSize: 10, padding: '3px 8px', borderRadius: 20, display: 'inline-block', background: s.status === 'complete' ? 'rgba(16,185,129,0.15)' : s.status === 'active' ? 'rgba(0,194,255,0.15)' : 'rgba(100,116,139,0.15)' }}>
                {s.status === "complete" ? "✓ Done" : s.status === "active" ? "● Running" : "○ Queued"}
              </div>
            </StageCard>
            {i < displayStages.length - 1 && (
              <div style={{ width: 32, height: 2, background: i < activeIdx ? '#10B981' : i === activeIdx ? 'linear-gradient(90deg, #10B981, #00C2FF)' : 'rgba(255,255,255,0.08)' }} />
            )}
          </StageWrapper>
        ))}
      </div>

      {overview?.confidence_metrics && !compact && (
        <div style={{ background: 'rgba(123,47,255,0.06)', border: '1px solid rgba(123,47,255,0.15)', borderRadius: 12, padding: 20, marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#9B5FFF', textTransform: 'uppercase' }}>AI Confidence Score</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 24, fontWeight: 700, color: '#00C2FF' }}>{Math.round(overview.confidence_metrics.overall * 100)}%</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {Object.entries(overview.confidence_metrics.components).map(([key, val], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 11, color: '#94A3B8', width: 120, textTransform: 'capitalize' }}>{key.replace('_', ' ')}</div>
                <div style={{ flex: 1, height: 4, background: '#0F2040', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: `${val * 100}%`, background: '#00C2FF', borderRadius: 2 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && !overview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginTop: 32 }}>
          {SUPERVISORS.map((sv, i) => (
            <div key={i} style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14, textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, border: '2px solid', background: `${sv.color}22`, borderColor: sv.status === "active" ? sv.color : sv.status === "complete" ? "#10B981" : "rgba(255,255,255,0.08)" }}>
                <span style={{ color: sv.status === "pending" ? "#64748B" : sv.color }}>{sv.initial}</span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8' }}>{sv.name}</div>
              <div style={{ fontFamily: 'DM Mono', fontSize: 9, color: '#64748B' }}>{sv.role}</div>
            </div>
          ))}
        </div>
      )}
    </PipelineContainer>
  );
}

// ─── Screen: Landing ──────────────────────────────────────────────────────────
const Hero = styled.div`
  position: relative; overflow: hidden;
  height: calc(100vh - 120px);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 80px 40px;
`;
const HeroTitle = styled.h1`
  font-family: 'Cinzel', serif; font-weight: 900;
  font-size: clamp(48px, 8vw, 88px); line-height: 0.95;
  letter-spacing: -0.02em; margin-bottom: 24px; text-align: center;
  animation: ${css`${fadeUp} 0.6s 0.1s ease both`};
`;

function LandingScreen({ onStart = null }) {
  return (
    <Page>
      <Hero>
        <div style={{ position: 'absolute', inset: 0, zIndex: 0, background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,194,255,0.08) 0%, transparent 70%)' }} />
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 800 }}>
          <FadeInDiv style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 20, marginBottom: 32, background: 'rgba(0,194,255,0.08)', border: '1px solid rgba(0,194,255,0.2)', fontFamily: 'DM Mono', fontSize: 11, color: '#00C2FF', textTransform: 'uppercase' }}>
            <span>⚡</span> AI Agents Build · Human Experts Certify
          </FadeInDiv>
          <HeroTitle>
            <span style={{ background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'block' }}>Software at</span>
            <span style={{ background: 'linear-gradient(135deg, #00C2FF 0%, #9B5FFF 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'block' }}>Machine Speed.</span>
          </HeroTitle>
          <FadeInDiv delay="0.2s" style={{ fontSize: 18, color: '#94A3B8', maxWidth: 560, margin: '0 auto 48px' }}>
            Submit a feature request. Five AI agents execute the full SDLC. Expert supervisors review every phase gate.
          </FadeInDiv>
          <FadeInDiv delay="0.3s" style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
            <button onClick={onStart} style={{ padding: '14px 32px', borderRadius: 8, fontSize: 15, fontWeight: 600, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', cursor: 'pointer' }}>Start Building →</button>
            {/* <button style={{ padding: '14px 32px', borderRadius: 8, fontSize: 15, fontWeight: 600, background: 'rgba(255,255,255,0.04)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}>View Demo</button> */}
          </FadeInDiv>
        </div>
      </Hero>
      {/* <div style={{ padding: '80px 40px', maxWidth: 1100, margin: '0 auto' }}>
        <SectionTitle style={{ textAlign: 'center' }}>SDLC Pipeline — Live</SectionTitle>
        <SectionSub style={{ textAlign: 'center' }}>Five agents execute, humans certify. Structurally guaranteed quality.</SectionSub>
        <PipelineViz />
      </div> */}
    </Page>
  );
}

// ─── Screen: PRD Upload ──────────────────────────────────────────────────────
const UploadZone = styled.div`
  border: 2px dashed rgba(0, 194, 255, 0.3);
  background: rgba(10, 22, 40, 0.4);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: rgba(0, 194, 255, 0.05);
    border-color: #00C2FF;
  }
`;

const FeatureCard = styled.div`
  background: #0A1628;
  border: 1px solid ${props =>
    props.selected ? '#00C2FF' :
    props.submitted && props.resubmitEnabled ? 'rgba(245,158,11,0.5)' :
    props.submitted ? 'rgba(245,158,11,0.2)' :
    'rgba(255,255,255,0.08)'};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  cursor: ${props => (props.submitted && !props.resubmitEnabled) ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  opacity: ${props => (props.submitted && !props.resubmitEnabled) ? 0.5 : 1};
  box-shadow: ${props =>
    props.selected ? '0 0 20px rgba(0,194,255,0.15)' :
    props.submitted && props.resubmitEnabled ? '0 0 12px rgba(245,158,11,0.12)' :
    'none'};
  &:hover {
    background: ${props => (props.submitted && !props.resubmitEnabled) ? '#0A1628' : 'rgba(255,255,255,0.02)'};
  }
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 2px solid ${props => props.selected ? '#00C2FF' : '#475569'};
  background: ${props => props.selected ? '#00C2FF' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
`;

const PulseIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  animation: ${pulseCyan} 2s infinite;
`;

// ─── Feature Edit Modal ─────────────────────────────────────────────────────
function FeatureEditModal({ feature, workflowId, onSave, onClose, allowEditSubmitted = false }) {
  const [title, setTitle] = useState(feature.title || '');
  const [description, setDescription] = useState(feature.description || '');
  const [acceptanceCriteria, setAcceptanceCriteria] = useState(
    Array.isArray(feature.acceptance_criteria) ? [...feature.acceptance_criteria] : []
  );
  const [sourceExcerpt, setSourceExcerpt] = useState(feature.source_excerpt || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedSpec, setSavedSpec] = useState(null);
  const [speckitStale, setSpeckitStale] = useState(false);
  const [showSpec, setShowSpec] = useState(false);

  const storyIdOrKey = feature.story_key || feature.feature_key || feature.id;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title,
        description,
        acceptance_criteria: acceptanceCriteria.filter(a => a.trim()),
        status: 'edited',
      };
      if (sourceExcerpt.trim()) payload.source_excerpt = sourceExcerpt;
      if (allowEditSubmitted) payload.allow_edit_submitted = true;

      const res = await agenticApi.editFeature(workflowId, storyIdOrKey, payload);
      setSavedSpec(res.updated_spec || null);
      setSpeckitStale(res.speckit_status === 'stale_after_feature_edit');
      onSave(res);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save changes.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const addCriterion = () => setAcceptanceCriteria(prev => [...prev, '']);
  const updateCriterion = (idx, val) => setAcceptanceCriteria(prev => prev.map((a, i) => i === idx ? val : a));
  const removeCriterion = (idx) => setAcceptanceCriteria(prev => prev.filter((_, i) => i !== idx));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#0A1628', border: '1px solid rgba(0,194,255,0.25)', borderRadius: 20, padding: '32px', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', position: 'relative', boxShadow: '0 24px 80px rgba(0,0,0,0.6)', animation: 'fadeUp 0.25s ease' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#00C2FF', textTransform: 'uppercase', marginBottom: 4 }}>Edit Feature</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#E2E8F0' }}>{feature.title}</div>
          </div>
          <button id="feature-edit-modal-close" onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#94A3B8', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
        </div>

        {/* Speckit stale warning */}
        {speckitStale && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
            <div style={{ fontSize: 13, color: '#F59E0B', lineHeight: 1.5 }}>Prior Spec Kit artifacts are now stale and need regeneration before final delivery.</div>
          </div>
        )}

        {/* Updated spec preview (collapsible) */}
        {savedSpec && (
          <div style={{ marginBottom: 20 }}>
            <button id="feature-edit-spec-toggle" onClick={() => setShowSpec(v => !v)} style={{ fontSize: 12, color: '#7B2FFF', background: 'rgba(123,47,255,0.1)', border: '1px solid rgba(123,47,255,0.2)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}>
              {showSpec ? '▼ Hide Updated Spec' : '▶ Show Updated Spec'}
            </button>
            {showSpec && (
              <div style={{ marginTop: 12, background: '#060D1A', border: '1px solid rgba(123,47,255,0.2)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontFamily: 'DM Mono', fontSize: 10, color: '#7B2FFF', marginBottom: 8, textTransform: 'uppercase' }}>Updated Spec</div>
                <div style={{ fontSize: 13, color: '#E2E8F0', fontWeight: 600, marginBottom: 4 }}>{savedSpec.title}</div>
                {savedSpec.summary && <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8, lineHeight: 1.5 }}>{savedSpec.summary}</div>}
                {savedSpec.acceptance_criteria?.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#94A3B8', fontSize: 12 }}>
                    {savedSpec.acceptance_criteria.map((ac, i) => <li key={i} style={{ marginBottom: 4 }}>{ac}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {saveError && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#EF4444' }}>{saveError}</div>
        )}

        {/* Title */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>Title</label>
          <input
            id="feature-edit-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 14px', color: '#fff', fontSize: 14, boxSizing: 'border-box' }}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>Description</label>
          <textarea
            id="feature-edit-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={4}
            style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 14px', color: '#fff', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>

        {/* Acceptance Criteria */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>Acceptance Criteria</label>
            <button id="feature-edit-add-criterion" onClick={addCriterion} style={{ fontSize: 11, color: '#00C2FF', background: 'rgba(0,194,255,0.1)', border: '1px solid rgba(0,194,255,0.2)', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>+ Add</button>
          </div>
          {acceptanceCriteria.length === 0 && (
            <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>No criteria defined — click + Add above.</div>
          )}
          {acceptanceCriteria.map((ac, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                value={ac}
                onChange={e => updateCriterion(idx, e.target.value)}
                placeholder={`Criterion ${idx + 1}`}
                style={{ flex: 1, background: '#0F2040', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '10px 12px', color: '#fff', fontSize: 13 }}
              />
              <button onClick={() => removeCriterion(idx)} style={{ width: 34, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.07)', borderRadius: 6, color: '#EF4444', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          ))}
        </div>

        {/* Source Excerpt */}
        <div style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>Source Excerpt <span style={{ color: '#475569', textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
          <textarea
            id="feature-edit-source-excerpt"
            value={sourceExcerpt}
            onChange={e => setSourceExcerpt(e.target.value)}
            rows={2}
            placeholder="Relevant quote from the original PRD..."
            style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button id="feature-edit-cancel" onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button
            id="feature-edit-save"
            onClick={handleSave}
            disabled={saving || !title.trim()}
            style={{ flex: 2, padding: '12px', borderRadius: 8, background: saving || !title.trim() ? '#1E293B' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 700, cursor: saving || !title.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {saving && <Spinner size="13px" />}
            {saving ? 'Saving...' : savedSpec ? '✓ Saved — Save Again' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Duplicate Confirm Dialog ────────────────────────────────────────────────
function DuplicateConfirmDialog({ conflict, onConfirm, onCancel, loading = false }) {
  if (!conflict) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#0A1628', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 20, padding: '36px', width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ fontSize: 36, marginBottom: 14, textAlign: 'center' }}>⚠️</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#E2E8F0', marginBottom: 10, textAlign: 'center' }}>Already Submitted Features</div>
        <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20, textAlign: 'center', lineHeight: 1.6 }}>
          The following features were already submitted and may have generated child workflows. Re-submitting will regenerate them.
        </div>
        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
          {(conflict.keys || conflict.ids || []).map((key, idx) => (
            <div key={idx} style={{ fontSize: 13, color: '#F59E0B', padding: '5px 0', borderBottom: idx < (conflict.keys || conflict.ids).length - 1 ? '1px solid rgba(245,158,11,0.1)' : 'none' }}>
              {key}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button id="duplicate-confirm-cancel" onClick={onCancel} disabled={loading} style={{ flex: 1, padding: '12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button
            id="duplicate-confirm-resubmit"
            onClick={onConfirm}
            disabled={loading}
            style={{ flex: 2, padding: '12px', borderRadius: 8, background: loading ? '#1E293B' : 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#fff', border: 'none', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {loading && <Spinner size="13px" />}
            {loading ? 'Re-submitting...' : 'Re-submit Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Screen: PRD Upload ──────────────────────────────────────────────────────
function PRDUploadScreen({ initialContext = null, onComplete = null, onReturn = null, setScreen }) {
  const [step, setStep] = useState('upload'); // 'upload', 'analyzing', 'selection', 'submitting', 'success'
  const [prdText, setPrdText] = useState('');
  const [prdTitle, setPrdTitle] = useState(initialContext?.prd_title || '');
  const [prdFile, setPrdFile] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [specData, setSpecData] = useState(null);
  const [extractedFeatures, setExtractedFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]); // ordered array of IDs
  const [submissionResult, setSubmissionResult] = useState(null);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [selectionStatusCounts, setSelectionStatusCounts] = useState(null);
  const [selectionHint, setSelectionHint] = useState('');
  const [editingFeature, setEditingFeature] = useState(null);
  const [allowEditSubmitted, setAllowEditSubmitted] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitOverrides, setResubmitOverrides] = useState({});
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (initialContext?.prd_title) {
      setPrdTitle(initialContext.prd_title);
    }
    const fetchExistingFeatures = async () => {
      if (initialContext?.workflow_id) {
        setWorkflowId(initialContext.workflow_id);
        setStep('selection');
        try {
          const featuresRes = await agenticApi.getWorkflowFeaturesV2(initialContext.workflow_id);
          setSelectionStatusCounts(featuresRes.selection_status_counts || null);
          setSelectionHint(featuresRes.selection_hint || '');
          
          const apiFeatures = featuresRes.features || featuresRes.selectable_features || featuresRes.feature_requests || [];
          if (apiFeatures && apiFeatures.length > 0) {
            setExtractedFeatures(apiFeatures);
          }
          
          const stories = await agenticApi.getStories(initialContext.workflow_id);
          setSpecData(stories);
        } catch (e) {
          console.error("Failed to load existing features", e);
        }
      }
    };
    fetchExistingFeatures();
  }, [initialContext]);

  const handleUpload = async () => {
    if (!prdText && !prdFile) return;
    setStep('analyzing');
    try {
      const userData = user?.data?.[0] || {};
      const payload = {
        prd_title: prdTitle || "Agentic App Build",
        prd_text: prdText || undefined,
        document: prdFile || undefined,
        manager_id: userData.emp_id || 1618,
        org_code: userData.org_code || "CLIENT#DEMO",
        pod_id: userData.pod_id || 47,
        approver_id: userData.emp_id || 1618,
        approver_name: userData.firstName || "User",
        application_id: initialContext?.application_id || undefined,
        parent_workflow_id: initialContext?.workflow_id || undefined,
        deployment_url: initialContext?.deployment_url || undefined,
        github_repo_url: initialContext?.github_repo_url || undefined,
        existing_app_deployment_url: initialContext?.deployment_url || undefined,
        existing_app_repo_url: initialContext?.github_repo_url || undefined,
        existing_app_s3_object_key: initialContext?.existing_app_s3_object_key || undefined,
        existing_app_commit_sha: initialContext?.existing_app_commit_sha || undefined,
        continuation_mode: initialContext ? 'existing_app_feature' : 'new_app',
        approval_required: approvalRequired,
        deploy_app: true,
        auto_approve: false
      };

      const res = await agenticApi.prdFeatureRequests(payload);
      setWorkflowId(res.workflow_id);
      setTaskId(res.task_id);
      setApplicationId(res.application_id);

      // Call V2 Features API to get selection_status_counts, selection_hint, and features with status
      try {
        if (res.workflow_id) {
          const featuresRes = await agenticApi.getWorkflowFeaturesV2(res.workflow_id);
          setSelectionStatusCounts(featuresRes.selection_status_counts || null);
          setSelectionHint(featuresRes.selection_hint || '');
          
          const apiFeatures = featuresRes.features || featuresRes.selectable_features || featuresRes.feature_requests || [];
          if (apiFeatures && apiFeatures.length > 0) {
            setExtractedFeatures(apiFeatures);
          } else {
            setExtractedFeatures(res.selectable_features || []);
          }
        } else {
          setExtractedFeatures(res.selectable_features || []);
        }
      } catch (err) {
        console.error("Failed to fetch workflow features v2", err);
        setExtractedFeatures(res.selectable_features || []);
      }

      try {
        if (res.workflow_id) {
          const stories = await agenticApi.getStories(res.workflow_id);
          setSpecData(stories);
        }
      } catch (e) { console.error("Failed to fetch stories", e); }

      setStep('selection');
    } catch (err) {
      console.error("PRD analysis failed", err);
      setStep('upload');
      alert("Analysis failed. Please check your PRD and try again.");
    }
  };

  // Toggle selection; if already selected remove it, else append at end (lowest priority)
  const toggleFeature = (id) => {
    const feat = extractedFeatures.find(f => f.id === id);
    const isSubmitted = feat?.selection_status === 'submitted' || feat?.status === 'submitted';

    if (isSubmitted) {
      const isOverridden = !!resubmitOverrides[id];
      if (!isOverridden) {
        // Toggle resubmit override to true and select
        setResubmitOverrides(prev => ({ ...prev, [id]: true }));
        setSelectedFeatures(prev => prev.includes(id) ? prev : [...prev, id]);
      } else {
        // Toggle resubmit override to false and deselect
        setResubmitOverrides(prev => ({ ...prev, [id]: false }));
        setSelectedFeatures(prev => prev.filter(f => f !== id));
      }
    } else {
      setSelectedFeatures(prev =>
        prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
      );
    }
  };

  // Move a selected feature up in priority order
  const movePriority = (e, id, dir) => {
    e.stopPropagation();
    setSelectedFeatures(prev => {
      const idx = prev.indexOf(id);
      if (idx === -1) return prev;
      const newArr = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= newArr.length) return prev;
      [newArr[idx], newArr[swapIdx]] = [newArr[swapIdx], newArr[idx]];
      return newArr;
    });
  };

  const handleSubmit = async (resubmitOverride = false) => {
    if (selectedFeatures.length === 0) return;

    if (!resubmitOverride) {
      const submittedSelected = extractedFeatures.filter(f => selectedFeatures.includes(f.id) && (f.selection_status === 'submitted' || f.status === 'submitted'));
      if (submittedSelected.length > 0) {
        setConflict({
          keys: submittedSelected.map(f => f.title),
          ids: submittedSelected.map(f => f.id)
        });
        return;
      }
    }

    setStep('submitting');
    setResubmitting(true);
    try {
      // Build priority map: first selected = priority 1
      const selected_feature_priorities = {};
      selectedFeatures.forEach((id, idx) => { selected_feature_priorities[String(id)] = idx + 1; });

      const payload = {
        selected_story_ids: selectedFeatures,
        selected_feature_priorities,
        deploy_app: true,
        auto_approve: false,
        approval_required: approvalRequired
      };

      if (resubmitOverride) {
        payload.allow_resubmit_selected = true;
      }

      const res = await agenticApi.submitSelectedFeatures(workflowId, payload);
      setSubmissionResult(res);
      setConflict(null);
      setStep('success');
      if (onComplete) onComplete(res);
    } catch (err) {
      console.error("Submission failed", err);
      if (err?.response?.status === 409) {
        const conflictData = err.response.data?.conflict || err.response.data || {};
        setConflict({
          keys: conflictData.keys || conflictData.story_keys || conflictData.ids || selectedFeatures.map(id => {
            const f = extractedFeatures.find(feat => feat.id === id);
            return f ? f.title : id;
          }),
          ids: conflictData.ids || conflictData.story_ids || selectedFeatures
        });
        setStep('selection');
      } else {
        setStep('selection');
        alert("Submission failed. Please try again.");
      }
    } finally {
      setResubmitting(false);
    }
  };

  const steps = [
    { title: 'Upload PRD', desc: 'Provide requirements.', tutor: 'Upload your Product Requirements Document or paste the text so the Requirements Agent can analyze it.' },
    { title: 'Select Features', desc: 'Review extracted features.', tutor: 'We extracted these features from your PRD. Select the ones you want to build and set their priority order.' },
    { title: 'AI Execution', desc: 'Pipeline in progress.', tutor: 'The features have been submitted! Backend agents are currently executing the SDLC. Monitor the activity below.' },
    { title: 'Certification', desc: 'Approve in Supervisor.', tutor: 'Once built, a human must certify the changes. Go to the Supervisor workspace when ready.' }
  ];

  let currentStepIdx = 0;
  if (step === 'upload' || step === 'analyzing') currentStepIdx = 0;
  else if (step === 'selection' || step === 'submitting') currentStepIdx = 1;
  else if (step === 'success') currentStepIdx = 2;

  const handleNext = () => {
    if (currentStepIdx === 2 && setScreen) {
      setScreen('supervisor');
    }
  };

  return (
    <Page style={{ padding: '40px', paddingBottom: '100px', maxWidth: 900, margin: '0 auto' }}>
      <WorkflowStepper
        steps={steps}
        currentStepIdx={currentStepIdx}
        onNext={currentStepIdx === 2 ? handleNext : null}
        nextLabel={currentStepIdx === 2 ? "Next: Go to Supervisor" : null}
      />

      {step === 'upload' && (
        <FadeInDiv>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#94A3B8', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Product/PRD Title</label>
            <input
              style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', color: '#fff', marginBottom: 20 }}
              placeholder="e.g. Daily Todo Tracker"
              value={prdTitle}
              onChange={(e) => setPrdTitle(e.target.value)}
            />
          </div>

          <label style={{ display: 'block', fontSize: 12, color: '#94A3B8', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Requirements Text</label>
          <textarea
            style={{ width: '100%', minHeight: 160, background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '20px', color: '#fff', marginBottom: 24, fontFamily: 'DM Mono', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
            placeholder="Paste your PRD text here or use the upload zone below..."
            value={prdText}
            onChange={(e) => setPrdText(e.target.value)}
          />

          <UploadZone onClick={() => document.getElementById('prd-file-input').click()}>
            <input
              type="file"
              id="prd-file-input"
              style={{ display: 'none' }}
              onChange={(e) => setPrdFile(e.target.files[0])}
            />
            <div style={{ fontSize: 40, marginBottom: 16 }}>{prdFile ? '✅' : '📄'}</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              {prdFile ? prdFile.name : 'Click or Drag PRD File'}
            </div>
            <div style={{ fontSize: 13, color: '#94A3B8' }}>Supports .txt, .pdf, .docx (Max 10MB)</div>
          </UploadZone>

          {/* ── Product Mode Toggle ── */}
          <div style={{ marginTop: 28, background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: approvalRequired ? '#10B981' : '#00C2FF', marginBottom: 4 }}>
                {approvalRequired ? '🛡️ Human-Certified Mode' : '⚡ Automatic Mode'}
              </div>
              <div style={{ fontSize: 12, color: '#64748B' }}>
                {approvalRequired
                  ? 'A senior engineer must review and certify every phase before output ships.'
                  : 'AI agents build and deploy automatically with no approval gates.'}
              </div>
            </div>
            <div
              onClick={() => setApprovalRequired(v => !v)}
              style={{
                width: 52, height: 28, borderRadius: 14, cursor: 'pointer',
                background: approvalRequired ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)',
                position: 'relative', transition: 'background 0.3s', flexShrink: 0
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: approvalRequired ? 26 : 3,
                width: 22, height: 22, borderRadius: '50%', background: '#fff',
                transition: 'left 0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }} />
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <button
              disabled={!prdText && !prdFile}
              onClick={handleUpload}
              style={{ width: '100%', padding: '16px', borderRadius: 10, background: (prdText || prdFile) ? 'linear-gradient(135deg, #00C2FF, #7B2FFF)' : '#1E293B', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16, cursor: (prdText || prdFile) ? 'pointer' : 'not-allowed', boxSizing: 'border-box', display: 'block' }}
            >
              Analyze PRD →
            </button>
          </div>
        </FadeInDiv>
      )}

      {step === 'analyzing' && (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <PulseIcon>🧬</PulseIcon>
          <SectionTitle style={{ fontSize: 24 }}>Analyzing PRD...</SectionTitle>
          <SectionSub>Requirements Agent is breaking down your document into features.</SectionSub>
          <div style={{ width: '200px', height: '4px', background: '#0F2040', margin: '0 auto', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#00C2FF', animation: 'load 3s ease-in-out' }} />
          </div>
          <style>{`@keyframes load { from { width: 0%; } to { width: 100%; } }`}</style>
        </div>
      )}

      {step === 'selection' && (
        <FadeInDiv>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <SectionTitle>Select Features</SectionTitle>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setSelectedFeatures(extractedFeatures.filter(f => f.selection_status !== 'submitted' && f.status !== 'submitted').map(f => f.id))}
                style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Select All
              </button>
              <button
                onClick={() => {
                  setSelectedFeatures([]);
                  setResubmitOverrides({});
                }}
                style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(239,68,68,0.07)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          </div>
          <SectionSub>We extracted {extractedFeatures.length} feature requests from your PRD. Select and order which ones to submit.</SectionSub>

          {/* ── Status Count Bar ── */}
          {(() => {
            const counts = { pending: 0, edited: 0, submitted: 0 };
            extractedFeatures.forEach(f => {
              const status = f.selection_status || f.status || 'pending';
              if (status === 'submitted') counts.submitted++;
              else if (status === 'edited') counts.edited++;
              else counts.pending++;
            });
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: '10px 16px', alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>Status Summary:</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#00C2FF' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C2FF' }} />
                    {counts.pending} Pending
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#7B2FFF' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7B2FFF' }} />
                    {counts.edited} Edited
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#F59E0B' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }} />
                    {counts.submitted} Submitted
                  </div>
                </div>
                {selectionHint && (
                  <div style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'DM Mono', color: '#64748B', background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 4 }}>
                    {selectionHint}
                  </div>
                )}
              </div>
            );
          })()}

          {workflowId && specData && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <a href={agenticApi.downloadSpecUrl(workflowId, 'md')} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                Download Spec (.md)
              </a>
              <a href={agenticApi.downloadSpecUrl(workflowId, 'json')} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(123,47,255,0.1)', color: '#7B2FFF', border: '1px solid rgba(123,47,255,0.2)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                Download Spec (.json)
              </a>
            </div>
          )}

          {/* ── Priority queue ── */}
          {selectedFeatures.length > 0 && (
            <div style={{ background: 'rgba(0,194,255,0.04)', border: '1px solid rgba(0,194,255,0.15)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#00C2FF', fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>Build Priority Order</div>
              {selectedFeatures.map((id, idx) => {
                const feat = extractedFeatures.find(f => f.id === id);
                if (!feat) return null;
                return (
                  <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: idx < selectedFeatures.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{idx + 1}</div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{feat.title}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={(e) => movePriority(e, id, -1)} disabled={idx === 0} style={{ width: 24, height: 24, borderRadius: 4, border: 'none', background: idx === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)', color: idx === 0 ? '#475569' : '#94A3B8', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 12 }}>▲</button>
                      <button onClick={(e) => movePriority(e, id, 1)} disabled={idx === selectedFeatures.length - 1} style={{ width: 24, height: 24, borderRadius: 4, border: 'none', background: idx === selectedFeatures.length - 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)', color: idx === selectedFeatures.length - 1 ? '#475569' : '#94A3B8', cursor: idx === selectedFeatures.length - 1 ? 'default' : 'pointer', fontSize: 12 }}>▼</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {extractedFeatures.map(f => {
            const priorityIdx = selectedFeatures.indexOf(f.id);
            const isSelected = priorityIdx !== -1;
            const isSubmitted = f.selection_status === 'submitted' || f.status === 'submitted';
            const isResubmitEnabled = !!resubmitOverrides[f.id];

            const status = f.selection_status || f.status || 'pending';
            let badgeColor = '#94A3B8';
            let badgeBg = 'rgba(100,116,139,0.1)';
            if (status === 'pending') {
              badgeColor = '#00C2FF';
              badgeBg = 'rgba(0,194,255,0.15)';
            } else if (status === 'edited') {
              badgeColor = '#7B2FFF';
              badgeBg = 'rgba(123,47,255,0.15)';
            } else if (status === 'submitted') {
              badgeColor = '#F59E0B';
              badgeBg = 'rgba(245,158,11,0.15)';
            }

            return (
              <FeatureCard
                key={f.id}
                selected={isSelected}
                submitted={isSubmitted}
                resubmitEnabled={isResubmitEnabled}
                onClick={() => toggleFeature(f.id)}
                style={{ flexDirection: 'column', alignItems: 'flex-start' }}
                title={isSubmitted && !isResubmitEnabled ? "Already submitted" : ""}
              >
                <div style={{ display: 'flex', gap: 16, width: '100%', alignItems: 'center' }}>
                  <Checkbox selected={isSelected} style={{ display: isSubmitted && !isResubmitEnabled ? 'none' : 'flex' }}>
                    {isSelected && "✓"}
                  </Checkbox>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{f.title}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {isSelected && (
                          <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(0,194,255,0.15)', color: '#00C2FF', fontWeight: 700 }}>#{priorityIdx + 1}</div>
                        )}
                        <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: badgeBg, color: badgeColor, textTransform: 'uppercase', fontWeight: 700 }}>{status}</div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const isSub = status === 'submitted';
                            if (isSub) {
                              if (window.confirm("This feature has already been submitted. Editing it may make prior Spec Kit artifacts stale. Do you want to edit it anyway?")) {
                                setEditingFeature(f);
                                setAllowEditSubmitted(true);
                              }
                            } else {
                              setEditingFeature(f);
                              setAllowEditSubmitted(false);
                            }
                          }}
                          style={{ background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: '2px 6px', fontSize: 13, display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                          onMouseEnter={(e) => e.target.style.color = '#00C2FF'}
                          onMouseLeave={(e) => e.target.style.color = '#94A3B8'}
                          title="Edit feature"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: '#94A3B8' }}>{f.description}</div>
                    {isSubmitted && (
                      <div style={{ fontSize: 11, color: isResubmitEnabled ? '#F59E0B' : '#64748B', display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <span>{isResubmitEnabled ? '⚠️ Re-submit override active' : '🔒 Already submitted — click card to enable re-submission'}</span>
                      </div>
                    )}
                  </div>
                </div>

                {f.acceptance_criteria && f.acceptance_criteria.length > 0 && (
                  <div style={{ marginTop: 16, paddingLeft: 38, width: '100%' }}>
                    <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Acceptance Criteria:</div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: '#94A3B8', fontSize: 12 }}>
                      {f.acceptance_criteria.map((ac, idx) => (
                        <li key={idx} style={{ marginBottom: 4 }}>{ac}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </FeatureCard>
            );
          })}

          <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
            <button
              onClick={() => setStep('upload')}
              style={{ flex: 1, padding: '16px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, cursor: 'pointer' }}
            >
              Back
            </button>
            <button
              disabled={selectedFeatures.length === 0}
              onClick={() => handleSubmit(false)}
              style={{ flex: 2, padding: '16px', borderRadius: 10, background: selectedFeatures.length > 0 ? 'linear-gradient(135deg, #00C2FF, #7B2FFF)' : '#1E293B', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16, cursor: selectedFeatures.length > 0 ? 'pointer' : 'not-allowed' }}
            >
              {approvalRequired ? '🛡️' : '⚡'} Submit {selectedFeatures.length} Feature{selectedFeatures.length !== 1 ? 's' : ''} →
            </button>
          </div>
        </FadeInDiv>
      )}

      {step === 'submitting' && (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 24 }}>🚀</div>
          <SectionTitle style={{ fontSize: 24 }}>Launching Pipeline...</SectionTitle>
          <SectionSub>Registering your features with the Agentic AI network.</SectionSub>
        </div>
      )}

      {step === 'success' && (
        <FadeInDiv>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <SectionTitle>Submission Successful!</SectionTitle>
            <SectionSub>Your feature requests are now live in the Agentic AI network.</SectionSub>
          </div>

          <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '32px' }}>
            <AgenticWorkerView
              workflowId={submissionResult?.workflow_id || workflowId}
              taskId={submissionResult?.task_id || taskId}
              initialTab="app"
              metadata={{ application_name: "New PRD Build" }}
            />
          </div>

          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              onClick={() => onReturn ? onReturn() : window.location.reload()}
              style={{ padding: '14px 32px', borderRadius: 8, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
            >
              Return to Dashboard
            </button>
          </div>
        </FadeInDiv>
      )}

      {editingFeature && (
        <FeatureEditModal
          feature={editingFeature}
          workflowId={workflowId}
          allowEditSubmitted={allowEditSubmitted}
          onClose={() => {
            setEditingFeature(null);
            setAllowEditSubmitted(false);
          }}
          onSave={(updatedRes) => {
            const updatedStory = updatedRes.story || updatedRes.feature_request || updatedRes;
            setExtractedFeatures(prev => prev.map(f => {
              const fKey = f.story_key || f.feature_key || f.id;
              const uKey = updatedStory.story_key || updatedStory.feature_key || updatedStory.id;
              if (fKey === uKey || f.id === updatedStory.id) {
                return {
                  ...f,
                  ...updatedStory,
                  selection_status: updatedStory.selection_status || 'edited',
                  status: updatedStory.status || 'edited'
                };
              }
              return f;
            }));
            if (updatedRes.selection_status_counts) {
              setSelectionStatusCounts(updatedRes.selection_status_counts);
            } else {
              agenticApi.getWorkflowFeaturesV2(workflowId).then(v2 => {
                setSelectionStatusCounts(v2.selection_status_counts);
              }).catch(e => console.error("Error refreshing counts after edit", e));
            }
          }}
        />
      )}

      {conflict && (
        <DuplicateConfirmDialog
          conflict={conflict}
          onConfirm={() => handleSubmit(true)}
          onCancel={() => setConflict(null)}
          loading={resubmitting}
        />
      )}
    </Page>
  );
}

// ─── Screen: Dashboard ────────────────────────────────────────────────────────
const MetricCard = styled.div`
  background: #0A1628; border: 1px solid rgba(255,255,255,0.08); border-radius: 14px;
  padding: 24px; position: relative; overflow: hidden;
  &::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: ${props => props.grad}; }
`;

function ClientDashboard({ onEnhance = null, onNewRequest = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await agenticApi.getDashboard();
        setData(res);
      } catch (err) {
        console.error("Failed to fetch dashboard", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const metrics = [
    { label: "Applications", value: data?.counts?.applications || "0", trend: "+2 today", grad: "linear-gradient(90deg, #00C2FF, #7B2FFF)" },
    { label: "Workflows", value: data?.counts?.workflows || "0", trend: "Active now", grad: "linear-gradient(90deg, #10B981, #00C2FF)" },
    { label: "Stories", value: data?.counts?.stories || "0", trend: "Generated", grad: "linear-gradient(90deg, #7B2FFF, #C084FC)" },
    { label: "Deployments", value: data?.counts?.deployments || "0", trend: "Live", grad: "linear-gradient(90deg, #10B981, #059669)" },
  ];

  if (loading) return <Page style={{ padding: '40px' }}><SectionTitle>Loading Dashboard...</SectionTitle></Page>;

  return (
    <Page style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <SectionTitle>Agentic AI Dashboard</SectionTitle>
          <div style={{ color: '#94A3B8', fontSize: 14 }}>Global Health: Optimal · 12 Agents Active</div>
        </div>
        <button onClick={onNewRequest} style={{ height: 'fit-content', padding: '10px 24px', borderRadius: 8, background: '#00C2FF1A', border: '1px solid #00C2FF4D', color: '#00C2FF', fontWeight: 600, cursor: 'pointer' }}>+ New Request</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {metrics.map((m, i) => (
          <MetricCard key={i} grad={m.grad}>
            <div style={{ fontSize: 12, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 12 }}>{m.label}</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 36, fontWeight: 700 }}>{m.value}</div>
            <div style={{ fontSize: 11, color: '#10B981', marginTop: 4 }}>{m.trend}</div>
          </MetricCard>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, marginTop: 20 }}>
        <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 14, fontWeight: 600 }}>Recent Workflows</div>
          {(data?.recent_workflows || []).map((w, i) => (
            <div key={i} style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C2FF', marginRight: 16 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{w.workflow?.workflow_name}</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#64748B' }}>{w.workflow?.prd_type} · {new Date(w.workflow?.updated_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,194,255,0.1)', color: '#00C2FF' }}>{w.workflow?.current_status}</span>
                <button
                  onClick={() => onEnhance && onEnhance(w)}
                  style={{ padding: '6px 12px', borderRadius: 6, background: '#7B2FFF1A', border: '1px solid #7B2FFF4D', color: '#7B2FFF', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >
                  Enhance →
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 14, fontWeight: 600 }}>Live Activity</div>
          {(data?.recent_audit_events || []).map((a, i) => (
            <div key={i} style={{ padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,194,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>⚡</div>
              <div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{a.event_type} - {a.label}</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 10, color: '#64748B' }}>{new Date(a.created_at).toLocaleTimeString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}

// ─── Screen: Submit Request ───────────────────────────────────────────────────
function SubmitRequest({ setScreen }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [status, setStatus] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [estimates, setEstimates] = useState({ time: '~6–8 hrs', cost: '$207.00' });
  const [submissionResult, setSubmissionResult] = useState(null);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const { user } = useSelector((state) => state.auth);

  // AI Preview of stages and dynamic estimates
  useEffect(() => {
    const getPreview = async () => {
      if (title.length > 5) {
        try {
          const res = await agenticApi.previewTemplate({ feature_text: title });
          setPreview(res.template);

          // Dynamically calculate based on stages
          const numStages = res.template?.stages?.length || 5;
          const hours = Math.round(numStages * 1.5);
          const cost = (numStages * 41.40).toFixed(2);

          setEstimates({
            time: `~${hours - 1}–${hours + 1} hrs`,
            cost: `$${cost}`
          });
        } catch (e) { }
      }
    };
    const timer = setTimeout(getPreview, 1000);
    return () => clearTimeout(timer);
  }, [title]);

  const handleSubmit = async () => {
    if (!title || !desc) return;
    setStatus('submitting');
    try {
      const userData = user?.data?.[0] || {};
      const payload = {
        feature_text: `${title}: ${desc}`,
        manager_id: userData.emp_id || 1618,
        org_code: userData.org_code || "CLIENT#DEMO",
        pod_id: userData.pod_id || 47,
        approver_id: userData.emp_id || 1618,
        approver_name: userData.firstName || "User",
        auto_approve: false,
        deploy_app: true,
        approval_required: approvalRequired
      };
      const res = await agenticApi.submitFeature(payload);
      setSubmissionResult(res);
      setStatus('success');
    } catch (err) {
      console.error("Manual submission failed", err);
      setStatus('idle');
      alert("Failed to launch pipeline. Please try again.");
    }
  };

  const steps = [
    { title: 'Define Request', desc: 'Enter details & scope.', tutor: 'Fill out the feature title and scope below. This gives the AI agents the requirements to start building.' },
    { title: 'AI Execution', desc: 'Agents actively building.', tutor: 'Pipeline is running! The backend agents are writing code, testing, and preparing the deployment. You can monitor the live logs here.' },
    { title: 'Certification', desc: 'Human review required.', tutor: 'Once the agents finish, the feature awaits human certification. Proceed to the Supervisor workspace to approve or reject.' }
  ];

  const currentStepIdx = (status === 'idle' || status === 'submitting') ? 0 : 1;

  const handleNext = () => {
    if (currentStepIdx === 1 && setScreen) {
      setScreen('supervisor');
    }
  };

  if (status === 'success') {
    return (
      <Page style={{ padding: '40px', maxWidth: 1000, margin: '0 auto' }}>
        <WorkflowStepper
          steps={steps}
          currentStepIdx={currentStepIdx}
          onNext={currentStepIdx === 1 ? handleNext : null}
          nextLabel={currentStepIdx === 1 ? "Next: Go to Supervisor" : null}
        />
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🚀</div>
          <SectionTitle>Pipeline Launched!</SectionTitle>
          <SectionSub>Your manual feature request is now being processed by the AI agents.</SectionSub>
        </div>

        <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '32px' }}>
          <AgenticWorkerView
            workflowId={submissionResult?.workflow_id}
            initialTab="activity"
            metadata={{ application_name: title }}
          />
        </div>

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <button
            onClick={() => setStatus('idle')}
            style={{ padding: '14px 32px', borderRadius: 8, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
          >
            Submit Another Request
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page style={{ padding: '40px', maxWidth: 800, margin: '0 auto' }}>
      <WorkflowStepper
        steps={steps}
        currentStepIdx={currentStepIdx}
      />
      <div style={{ background: '#0A1628', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 20, padding: '40px' }}>
        <SectionTitle>Submit Feature Request</SectionTitle>
        <SectionSub>AI agents begin execution immediately. Human experts certify each step.</SectionSub>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Feature Title</label>
          <input
            style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', color: '#fff' }}
            placeholder="e.g. Real-time Notification System"
            value={title}
            onChange={(e) => {
              const val = e.target.value;
              setTitle(val);
            }}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, color: '#94A3B8', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>Scope & Requirements</label>
          <textarea
            style={{ width: '100%', minHeight: 140, background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', color: '#fff' }}
            placeholder="Describe the feature in detail..."
            value={desc}
            onChange={(e) => {
              const val = e.target.value;
              setDesc(val);
            }}
          />
        </div>
        {/* 
        <div style={{ background: '#0F2040', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>ESTIMATED DELIVERY</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 22, fontWeight: 700, color: '#00C2FF' }}>{estimates.time}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 4 }}>AI EXECUTION COST</div>
            <div style={{ fontFamily: 'DM Mono', fontSize: 14, color: '#10B981' }}>{estimates.cost}</div>
          </div>
        </div>
        */}

        {preview && (
          <div style={{ marginBottom: 24, padding: '16px', background: 'rgba(0,194,255,0.05)', borderRadius: 12, border: '1px solid rgba(0,194,255,0.2)' }}>
            <div style={{ fontSize: 11, color: '#00C2FF', fontWeight: 600, marginBottom: 12, textTransform: 'uppercase' }}>Predicted AI Pipeline Stages</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {preview.stages.map((s, i) => (
                <div key={i} style={{ padding: '4px 10px', background: '#0A1628', borderRadius: 4, fontSize: 11, border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}>{s}</div>
              ))}
            </div>
          </div>
        )}

        {/* ── Product Mode Toggle ── */}
        <div style={{ marginBottom: 20, background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: approvalRequired ? '#10B981' : '#00C2FF', marginBottom: 3 }}>
              {approvalRequired ? '🛡️ Human-Certified Mode' : '⚡ Automatic Mode'}
            </div>
            <div style={{ fontSize: 11, color: '#64748B' }}>
              {approvalRequired ? 'Expert review required before code ships.' : 'Agents build & deploy instantly, no approval gates.'}
            </div>
          </div>
          <div
            onClick={() => setApprovalRequired(v => !v)}
            style={{ width: 52, height: 28, borderRadius: 14, cursor: 'pointer', background: approvalRequired ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)', position: 'relative', transition: 'background 0.3s', flexShrink: 0 }}
          >
            <div style={{ position: 'absolute', top: 3, left: approvalRequired ? 26 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={status === 'submitting' || !title || !desc}
          style={{ width: '100%', padding: '16px', borderRadius: 10, background: (status === 'submitting' || !title || !desc) ? '#1E293B' : 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 16, cursor: (status === 'submitting' || !title || !desc) ? 'not-allowed' : 'pointer' }}
        >
          {status === 'submitting' ? 'Launching...' : `${approvalRequired ? '🛡️' : '⚡'} Launch AI Pipeline →`}
        </button>
      </div>
    </Page>
  );
}

// ─── Reusable Agentic Worker View ──────────────────────────────────────────
function AgenticWorkerView({ workflowId, taskId, stageName, initialTab = "spec", showActions = false, onAction = null, metadata = {} }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [artifacts, setArtifacts] = useState([]);
  const [health, setHealth] = useState(null);
  const [parentHealth, setParentHealth] = useState(null);
  const [featureStatus, setFeatureStatus] = useState(null);
  const [releases, setReleases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedReleaseUrl, setSelectedReleaseUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workflowId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [artRes, healthRes, relRes, featureRes] = await Promise.all([
          agenticApi.getStageArtifacts(workflowId, stageName || 'coding'),
          agenticApi.getWorkflowHealth(workflowId),
          agenticApi.getWorkflowReleases(workflowId),
          taskId ? agenticApi.getFeatureStatus(taskId).catch(() => null) : Promise.resolve(null)
        ]);

        setArtifacts(artRes.artifacts || []);
        setHealth(healthRes);
        setReleases(relRes.releases || []);
        if (featureRes) setFeatureStatus(featureRes);

        // Use events from health as fallback for notifications
        let rawEvents = healthRes?.events || healthRes?.audit_events || healthRes?.recent_audit_events || [];

        // If no standard events array, synthesize activity from health response structure
        if (rawEvents.length === 0 && healthRes) {
          const syntheticEvents = [];
          const timestamp = new Date().toISOString();

          if (healthRes.current_status) {
            syntheticEvents.push({ created_at: timestamp, message: `Workflow status: ${healthRes.current_status}`, source: 'System', type: 'info' });
          }
          if (healthRes.health && healthRes.health.details) {
            syntheticEvents.push({ created_at: timestamp, message: `Health: ${healthRes.health.details}`, source: healthRes.health.service || 'Health Check', type: healthRes.health.status === 'blocked' ? 'error' : 'info' });
          }
          if (healthRes.release_gates?.gates) {
            healthRes.release_gates.gates.forEach(gate => {
              syntheticEvents.push({ created_at: timestamp, message: `Gate [${gate.name}]: ${gate.details}`, source: 'Release Gate', type: gate.status === 'fail' ? 'error' : 'success' });
            });
          }
          if (healthRes.codegen_validation?.results) {
            healthRes.codegen_validation.results.forEach(res => {
              syntheticEvents.push({ created_at: timestamp, message: `Codegen [${res.name}]`, source: 'Validation', type: res.status === 'fail' ? 'error' : 'info' });
            });
          }
          rawEvents = syntheticEvents;
        }

        const allNotes = [...rawEvents].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setNotifications(allNotes);

        const latestApp = (relRes.releases || []).find(r => r.page_variant === 'landing' || r.release_kind === 'app') || (relRes.releases || [])[0];
        setSelectedReleaseUrl(latestApp?.deployment_url || healthRes?.deployment_url || healthRes?.live_app_url);

        if (healthRes?.workflow?.parent_workflow_id) {
          try {
            const pHealth = await agenticApi.getWorkflowHealth(healthRes.workflow.parent_workflow_id);
            setParentHealth(pHealth);
          } catch (pe) { }
        }
      } catch (err) {
        console.error("AgenticWorkerView fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const timer = setInterval(fetchData, 8000); // Polling faster (8s) for better "Live" feel
    return () => clearInterval(timer);
  }, [workflowId, stageName]);

  if (loading && !health) return <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Initializing Observation Deck...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* GAP 3: Awaiting Approval Banner */}
      {(featureStatus?.pending_approval_stage || health?.current_status === 'AWAITING_APPROVAL') && (
        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, color: '#F59E0B' }}>
          <BlinkingDot style={{ background: '#F59E0B' }} />
          <span style={{ fontWeight: 600 }}>Pipeline paused — awaiting human certification for [{featureStatus?.pending_approval_stage || 'current stage'}].</span>
        </div>
      )}

      {showActions && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 28, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#64748B' }}>{metadata.application_name} · Workflow ID: {workflowId?.substring(0, 8)}</div>
              <SectionTitle style={{ fontSize: 24, margin: 0 }}>{stageName || 'Execution'} Review</SectionTitle>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20,
              background: health?.deployment_status === 'running' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
              border: `1px solid ${health?.deployment_status === 'running' ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`,
              fontSize: 11, fontWeight: 700, color: health?.deployment_status === 'running' ? '#10B981' : '#F59E0B'
            }}>
              {health?.deployment_status === 'running' ? <BlinkingDot /> : '●'} {health?.deployment_status?.toUpperCase() || 'INITIALIZING'}
            </div>

            {/* GAP 5 & 6: QSI and Certified badge */}
            {health?.workflow?.approval_required && (health?.workflow?.qsi_score || featureStatus?.approval?.qsi_score) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20,
                background: 'rgba(123,47,255,0.1)', border: '1px solid rgba(123,47,255,0.3)',
                fontSize: 11, fontWeight: 700, color: '#9B5FFF'
              }}>
                QSI: {health?.workflow?.qsi_score || featureStatus?.approval?.qsi_score} · Certified ✅
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            {/* GAP 4: PR / Repo Link */}
            {(health?.github_pull_request_url || health?.generated_app_repo_url || health?.github_commit_sha) && (
              <a href={health?.github_pull_request_url || health?.generated_app_repo_url || '#'} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: 'rgba(100,116,139,0.1)', color: '#94A3B8', border: '1px solid rgba(100,116,139,0.2)', fontWeight: 600, textDecoration: 'none' }}>
                {health?.github_pull_request_url ? 'View PR →' : health?.generated_app_repo_url ? 'View Repo →' : `Commit: ${health?.github_commit_sha?.substring(0, 7)}`}
              </a>
            )}
            {selectedReleaseUrl && (
              <a href={selectedReleaseUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', fontWeight: 600, textDecoration: 'none' }}>
                View Live App 🚀
              </a>
            )}
            {health?.deployment_status !== 'running' && health?.deployment_status !== 'completed' && (
              <button
                onClick={async () => {
                  try {
                    await agenticApi.deployApp(workflowId, { submit_all: true });
                    alert("Deployment triggered!");
                  } catch (e) {
                    alert("Deployment failed: " + e.message);
                  }
                }}
                style={{ padding: '10px 20px', borderRadius: 8, background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', fontWeight: 600, cursor: 'pointer' }}
              >
                Deploy Now
              </button>
            )}
            <button
              onClick={() => onAction && onAction('enhance', health)}
              style={{ padding: '10px 20px', borderRadius: 8, background: 'rgba(123,47,255,0.1)', color: '#7B2FFF', border: '1px solid rgba(123,47,255,0.2)', fontWeight: 600, cursor: 'pointer' }}
            >
              + Add Feature
            </button>
            <button onClick={() => onAction && onAction('reject')} style={{ padding: '10px 20px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)', fontWeight: 600, cursor: 'pointer' }}>Reject</button>
            <button onClick={() => onAction && onAction('approve')} style={{ padding: '10px 24px', borderRadius: 8, background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Approve</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 24 }}>
        {["spec", "app", "activity", "diff"].map(t => (
          <div key={t} onClick={() => setActiveTab(t)} style={{ padding: '10px 4px', fontSize: 13, color: activeTab === t ? '#00C2FF' : '#94A3B8', borderBottom: activeTab === t ? '2px solid #00C2FF' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</div>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        {activeTab === 'diff' && (
          <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: 'DM Mono', fontSize: 12, color: '#00C2FF' }}>Artifacts for {stageName || 'Execution'}</div>
            <div style={{ padding: '20px', maxHeight: '600px', overflowY: 'auto' }}>
              {artifacts.length > 0 ? artifacts.map((art, idx) => (
                <div key={idx} style={{ marginBottom: 20 }}>
                  <div style={{ color: '#00C2FF', marginBottom: 8, fontSize: 11 }}># {art.artifact_type} ({art.direction})</div>
                  <pre style={{ background: 'rgba(0,0,0,0.2)', padding: 12, borderRadius: 8, overflowX: 'auto', whiteSpace: 'pre-wrap', fontSize: 12, color: '#94A3B8', fontFamily: 'DM Mono' }}>
                    {typeof art.payload === 'string' ? art.payload : JSON.stringify(art.payload, null, 2)}
                  </pre>
                </div>
              )) : <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>No artifacts found.</div>}
            </div>
          </div>
        )}

        {activeTab === 'spec' && <SupervisorSpecKitViewer workflowId={workflowId} />}

        {activeTab === 'app' && (
          <div style={{ height: '700px', display: 'grid', gridTemplateColumns: '250px 1fr', background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: '#0A1628', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase' }}>Release History</div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                {releases.length > 0 ? releases.map((rel, i) => (
                  <div key={i} onClick={() => setSelectedReleaseUrl(rel.deployment_url)} style={{ padding: '10px', borderRadius: 6, background: selectedReleaseUrl === rel.deployment_url ? 'rgba(0,194,255,0.1)' : 'transparent', border: selectedReleaseUrl === rel.deployment_url ? '1px solid rgba(0,194,255,0.3)' : '1px solid transparent', cursor: 'pointer', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: selectedReleaseUrl === rel.deployment_url ? '#00C2FF' : '#E2E8F0' }}>{rel.page_variant === 'landing' ? '🚀 Landing Page' : rel.release_kind === 'app' ? '📱 Application' : '📊 Tracker'}</div>
                    <div style={{ fontSize: 10, color: '#64748B', marginTop: 4 }}>v{rel.release_version || '1.0'}</div>
                  </div>
                )) : <div style={{ padding: '20px', fontSize: 11, color: '#64748B', textAlign: 'center' }}>No releases found.</div>}
              </div>
              <div style={{ padding: '12px', background: 'rgba(0,194,255,0.05)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 10, color: '#00C2FF', marginBottom: 4 }}>Status: {health?.deployment_status || 'Initializing'}</div>
                {(parentHealth?.deployment_url || parentHealth?.website_url) && (
                  <button onClick={() => setSelectedReleaseUrl(parentHealth.deployment_url || parentHealth.website_url)} style={{ width: '100%', padding: '6px', borderRadius: 4, background: '#00C2FF', color: '#fff', border: 'none', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>View Main App 🏢</button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'DM Mono', fontSize: 12, color: '#00C2FF' }}>Preview: {selectedReleaseUrl?.split('/').pop() || 'Live Deployment'}</div>
                {selectedReleaseUrl && <a href={selectedReleaseUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#94A3B8', textDecoration: 'none' }}>Open ↗</a>}
              </div>
              <div style={{ flex: 1 }}>
                {selectedReleaseUrl ? <iframe src={selectedReleaseUrl} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} title="Preview" /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>🚀 Deployment in Progress...</div>}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '24px', height: '600px', overflowY: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 20 }}>⚡ Pipeline Activity</div>
            {notifications.length > 0 ? notifications.map((note, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: 16 }}>
                <div style={{ minWidth: 80, fontSize: 10, color: '#64748B' }}>{new Date(note.created_at).toLocaleTimeString()}</div>
                <div>
                  <div style={{ fontSize: 13, color: '#E2E8F0' }}>{note.message || note.notification_text || note.label || 'Event logged'}</div>
                  <div style={{ fontSize: 10, color: note.type === 'error' ? '#EF4444' : '#10B981', marginTop: 4 }}>{note.source || note.event_type || 'Automation'}</div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', color: '#64748B', padding: '40px 0', fontSize: 13 }}>
                No activity logs available for this workflow yet.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Review Action Modal Component ───────────────────────────────────────────

function ReviewActionModal({ type, workflowId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [qsiScore, setQsiScore] = useState(90);
  const [reviewerRole, setReviewerRole] = useState('Senior Engineer');
  const [notes, setNotes] = useState('');
  const [ttlHours, setTtlHours] = useState(24);

  const qsiColor = qsiScore >= 80 ? '#10B981' : qsiScore >= 60 ? '#F59E0B' : '#EF4444';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (type === 'supervisor_approval') {
        const res = await agenticApi.approveWorkflow(workflowId, 'approve', {
          reviewer_role: 'supervisor',
          notes: notes || 'Approved for client review.',
          qsi_score: qsiScore,
          certification_status: 'certified'
        });
        onSuccess('Approved for client review successfully!', res);
      } else if (type === 'client_approval') {
        const res = await agenticApi.approveWorkflow(workflowId, 'approve', {
          reviewer_role: 'client',
          notes: notes || 'Client accepts final preview.',
          preview_ttl_hours: ttlHours
        });
        onSuccess('Client accepts final preview successfully!', res);
      } else if (type === 'supervisor_regenerate') {
        if (!notes.trim()) {
          alert('Please enter actionable feedback for the regeneration.');
          setLoading(false);
          return;
        }
        const res = await agenticApi.regenerateWorkflow(workflowId, {
          feedback: notes,
          stage_name: 'supervisor_review',
          auto_deploy_rebuild: true,
          deploy_app: true,
          approval_required: true,
          release_channel: 'preview'
        });
        onSuccess('Regeneration queued successfully!', res);
      }
    } catch (err) {
      alert('Operation failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 40, width: '100%', maxWidth: 520, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        {type === 'supervisor_approval' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🛡️</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Supervisor Certification</div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Reviewer Role</label>
              <select
                value={reviewerRole}
                onChange={e => setReviewerRole(e.target.value)}
                style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
              >
                {REVIEWER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>Quality Score Index (QSI)</label>
                <div style={{ fontFamily: 'Cinzel', fontSize: 24, fontWeight: 700, color: qsiColor }}>{qsiScore}</div>
              </div>
              <input
                type="range" min={0} max={100} value={qsiScore}
                onChange={e => setQsiScore(Number(e.target.value))}
                style={{ width: '100%', accentColor: qsiColor, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#475569', marginTop: 4 }}>
                <span>Critical (0)</span><span>Needs Work (50)</span><span>Excellent (100)</span>
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Approval Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Approved for client review..."
                style={{ width: '100%', minHeight: 80, background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px', color: '#fff', fontFamily: 'DM Mono', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          </>
        )}

        {type === 'client_approval' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🚀</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Client Approval</div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Preview Expiry (TTL)</label>
              <select
                value={ttlHours}
                onChange={e => setTtlHours(Number(e.target.value))}
                style={{ width: '100%', background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }}
              >
                <option value={12}>12 Hours</option>
                <option value={24}>24 Hours (Default)</option>
                <option value={48}>48 Hours</option>
                <option value={72}>72 Hours</option>
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Client accepts final preview..."
                style={{ width: '100%', minHeight: 80, background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px', color: '#fff', fontFamily: 'DM Mono', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          </>
        )}

        {type === 'supervisor_regenerate' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔁</div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Regenerate with Feedback</div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Actionable Feedback (Required)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add clearer SKU search filters, reorder alerts, and a stronger dashboard summary..."
                style={{ width: '100%', minHeight: 120, background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px', color: '#fff', fontFamily: 'DM Mono', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{ flex: 2, padding: '12px', borderRadius: 8, background: type === 'supervisor_regenerate' ? 'linear-gradient(135deg, #EF4444, #DC2626)' : 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', border: 'none', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 15 }}
          >
            {loading && <Spinner />}
            {type === 'supervisor_approval' ? '🛡️ Certify & Approve' : type === 'client_approval' ? '🚀 Client Approve' : '🔁 Submit Rebuild'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Screen: Workspace ────────────────────────────────────────────────────────
function SupervisorWorkspace({ initialContext = null, onAction = null }) {
  const [queue, setQueue] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [deliveryDetail, setDeliveryDetail] = useState(null);
  const [readinessFilter, setReadinessFilter] = useState('all'); // 'all', 'supervisor_ready', 'client_ready'

  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingDeploy, setLoadingDeploy] = useState(false);
  const [loadingRefreshQA, setLoadingRefreshQA] = useState(false);
  const [loadingHandoff, setLoadingHandoff] = useState(false);
  const [loadingDownloads, setLoadingDownloads] = useState({ md: false, json: false, zip: false });

  const [activeTab, setActiveTab] = useState('preview'); // 'preview', 'gates', 'deliverables', 'speckit'
  const [actionMessage, setActionMessage] = useState(null);
  const [modalType, setModalType] = useState(null); // 'supervisor_approval' | 'client_approval' | 'supervisor_regenerate'
  const [revisionWorkflowId, setRevisionWorkflowId] = useState(null);
  const [revisionTaskId, setRevisionTaskId] = useState(null);

  const { user } = useSelector((state) => state.auth);

  // Fetch Delivery Queue
  const fetchQueue = async () => {
    setLoadingQueue(true);
    try {
      const params = { limit: 50 };
      if (readinessFilter !== 'all') {
        params.readiness = readinessFilter;
      }
      const res = await agenticApi.getDeliveryQueue(params);
      setQueue(res.delivery_queue || []);

      // Auto-select first item or initialContext
      if (res.delivery_queue?.length > 0 && !selectedWorkflowId) {
        if (initialContext?.workflow_id && res.delivery_queue.some(q => q.workflow_id === initialContext.workflow_id)) {
          setSelectedWorkflowId(initialContext.workflow_id);
        } else {
          setSelectedWorkflowId(res.delivery_queue[0].workflow_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch delivery queue", err);
    } finally {
      setLoadingQueue(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, [readinessFilter]);

  // Fetch Detail when selected item changes
  const fetchDetail = async (wfId) => {
    if (!wfId) return;
    setLoadingDetail(true);
    try {
      const res = await agenticApi.getDeliveryDetail(wfId);
      setDeliveryDetail(res);
    } catch (err) {
      console.error("Failed to fetch delivery detail", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (selectedWorkflowId) {
      fetchDetail(selectedWorkflowId);
    }
  }, [selectedWorkflowId]);

  // Handle active initialContext tracking
  useEffect(() => {
    if (initialContext?.workflow_id) {
      setSelectedWorkflowId(initialContext.workflow_id);
    }
  }, [initialContext]);

  // Polling for revision workflow regeneration updates
  useEffect(() => {
    if (!revisionWorkflowId) return;
    const interval = setInterval(async () => {
      try {
        const details = await agenticApi.getDeliveryDetail(revisionWorkflowId);
        // Once status shows gates passed or ready for review, swap context
        if (details.delivery_status === 'ready_for_supervisor_review' || details.review_readiness?.supervisor_ready) {
          setDeliveryDetail(details);
          setSelectedWorkflowId(revisionWorkflowId);
          setRevisionWorkflowId(null);
          setRevisionTaskId(null);
          setActionMessage({
            type: 'approve',
            text: 'Regenerated build is successfully completed and ready for supervisor review!'
          });
          fetchQueue();
        }
      } catch (err) {
        console.error("Revision polling failed", err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [revisionWorkflowId]);

  // Refresh SQLite QA Evidence
  const handleRefreshQA = async () => {
    if (!selectedWorkflowId) return;
    setLoadingRefreshQA(true);
    try {
      const res = await agenticApi.refreshSqliteQa(selectedWorkflowId);
      setActionMessage({ type: 'approve', text: res.message || 'SQLite QA evidence refreshed successfully!' });
      await fetchDetail(selectedWorkflowId);
    } catch (err) {
      alert('QA Refresh failed: ' + err.message);
    } finally {
      setLoadingRefreshQA(false);
      setTimeout(() => setActionMessage(null), 6000);
    }
  };

  // Deploy or Redeploy App
  const handleDeploy = async () => {
    if (!selectedWorkflowId) return;
    setLoadingDeploy(true);
    try {
      const payload = {
        skip_codepipeline: true,
        release_channel: "preview",
        page_variant: "dashboard",
        approval_required: true
      };
      await agenticApi.deployApp(selectedWorkflowId, payload);
      setActionMessage({ type: 'approve', text: 'Generated app deployment successfully triggered to globldataprod.' });
      await fetchDetail(selectedWorkflowId);
    } catch (err) {
      alert('Deployment failed: ' + err.message);
    } finally {
      setLoadingDeploy(false);
      setTimeout(() => setActionMessage(null), 6000);
    }
  };

  // Create Supervisor Handoff
  const handleHandoff = async () => {
    if (!selectedWorkflowId) return;
    setLoadingHandoff(true);
    try {
      const res = await agenticApi.createSupervisorHandoff(selectedWorkflowId);
      setActionMessage({ type: 'approve', text: res.message || 'App explicitly marked ready for supervisor review!' });
      await fetchDetail(selectedWorkflowId);
    } catch (err) {
      alert('Handoff creation failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingHandoff(false);
      setTimeout(() => setActionMessage(null), 6000);
    }
  };

  // Handle Download Deliverables
  const triggerDownload = (type, url) => {
    setLoadingDownloads(prev => ({ ...prev, [type]: true }));
    try {
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => {
        setLoadingDownloads(prev => ({ ...prev, [type]: false }));
      }, 1000);
    }
  };

  // Handle Modal successes
  const handleModalSuccess = (msg, data = null) => {
    setModalType(null);
    setActionMessage({ type: 'approve', text: msg });
    setTimeout(() => setActionMessage(null), 6000);

    if (data?.rebuild_requested && data?.revision_workflow_id) {
      setRevisionWorkflowId(data.revision_workflow_id);
      setRevisionTaskId(data.revision_task_id);
      setActionMessage({
        type: 'rebuild',
        text: `Feedback queued. AI agents are actively rebuilding. Polling Revision ID: ${data.revision_workflow_id.substring(0, 8)}...`,
        liveAppUrl: data.live_app_url
      });
      if (onAction) onAction('revision', { workflow_id: data.revision_workflow_id, task_id: data.revision_task_id });
    } else {
      fetchQueue();
      if (selectedWorkflowId) fetchDetail(selectedWorkflowId);
    }
  };

  // Pre-calculate direct URLs
  const specBase = process.env.REACT_APP_CLIENT_AGENT || 'https://astrabase.artemy.ai';
  const specMdUrl = deliveryDetail?.downloads?.spec_markdown_url
    ? `${specBase}${deliveryDetail.downloads.spec_markdown_url}`
    : agenticApi.downloadSpecUrl(selectedWorkflowId, 'md');

  const specJsonUrl = deliveryDetail?.downloads?.spec_json_url
    ? `${specBase}${deliveryDetail.downloads.spec_json_url}`
    : agenticApi.downloadSpecUrl(selectedWorkflowId, 'json');

  const codeZipUrl = deliveryDetail?.downloads?.code_zip_url
    ? `${specBase}${deliveryDetail.downloads.code_zip_url}`
    : `${specBase}/agentic-workflows/${selectedWorkflowId}/code/download`;

  if (loadingQueue && queue.length === 0) return <Page style={{ padding: 40 }}><SectionTitle>Loading Inbox Queue...</SectionTitle></Page>;

  return (
    <Page style={{ height: 'calc(100vh - 64px)', display: 'grid', gridTemplateColumns: '320px 1fr' }}>

      {modalType && (
        <ReviewActionModal
          type={modalType}
          workflowId={selectedWorkflowId}
          onClose={() => setModalType(null)}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Inbox Panel (Left) */}
      <div style={{ background: '#0A1628', borderRight: '1px solid rgba(255,255,255,0.08)', padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Cinzel', fontSize: 18, fontWeight: 700 }}>Review Inbox</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{queue.length} active preview{queue.length !== 1 ? 's' : ''}</div>
          </div>
          <button
            onClick={fetchQueue}
            disabled={loadingQueue}
            style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
          >
            {loadingQueue ? <Spinner size="10px" /> : '↻'}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', background: '#060D1A', borderRadius: 8, padding: 3, border: '1px solid rgba(255,255,255,0.05)' }}>
          {[['all', 'All'], ['supervisor_ready', 'Supervisor'], ['client_ready', 'Client']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setReadinessFilter(val)}
              style={{ flex: 1, padding: '6px 0', border: 'none', background: readinessFilter === val ? 'linear-gradient(135deg, #00C2FF, #7B2FFF)' : 'transparent', color: readinessFilter === val ? '#fff' : '#94A3B8', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s' }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Inbox Queue Cards */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {queue.length > 0 ? queue.map((item, idx) => {
            const isSelected = item.workflow_id === selectedWorkflowId;
            return (
              <div
                key={item.workflow_id || idx}
                onClick={() => setSelectedWorkflowId(item.workflow_id)}
                style={{
                  padding: '16px',
                  borderRadius: 12,
                  background: isSelected ? 'rgba(0,194,255,0.08)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isSelected ? '#00C2FF' : 'rgba(255,255,255,0.06)'}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontFamily: 'DM Mono', color: '#00C2FF' }}>
                    ID: {item.workflow_id?.substring(0, 8)}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                    background: item.delivery_status === 'ready_for_supervisor_review' ? 'rgba(0, 194, 255, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                    color: item.delivery_status === 'ready_for_supervisor_review' ? '#00C2FF' : '#10B981',
                    textTransform: 'uppercase'
                  }}>
                    {item.delivery_status?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0', marginBottom: 6 }}>
                  {item.app_key || 'Generated Application'}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#64748B' }}>
                  <span>Supervisor: {item.supervisor_ready ? '✅' : '❌'}</span>
                  <span>Client: {item.client_ready ? '✅' : '❌'}</span>
                </div>
              </div>
            );
          }) : (
            <div style={{ padding: '40px 10px', textAlign: 'center', color: '#64748B', fontSize: 12 }}>
              No preview items in this filter queue.
            </div>
          )}
        </div>
      </div>

      {/* Details pane (Right) */}
      <div style={{ overflowY: 'auto', padding: '36px', display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' }}>

        {/* Banner notification */}
        {actionMessage && (
          <NotificationBanner type={actionMessage.type === 'rebuild' ? 'rebuild' : actionMessage.type}>
            <span style={{ fontSize: 14, color: actionMessage.type === 'approve' ? '#10B981' : actionMessage.type === 'rebuild' ? '#F59E0B' : '#EF4444', fontWeight: 600 }}>
              {actionMessage.text}
            </span>
            {actionMessage.liveAppUrl && (
              <a href={actionMessage.liveAppUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 16, fontSize: 12, color: '#00C2FF', textDecoration: 'none', fontWeight: 600 }}>View Deploy →</a>
            )}
          </NotificationBanner>
        )}

        {/* Loading detail mask */}
        {loadingDetail && !deliveryDetail && (
          <div style={{ height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 16, color: '#94A3B8' }}>
            <Spinner size="24px" />
            <span>Fetching Deployment details...</span>
          </div>
        )}

        {deliveryDetail && (
          <>
            {/* Header Details */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <SectionTitle style={{ fontSize: 24, margin: 0 }}>
                    {deliveryDetail.app_key || 'Generated Application'}
                  </SectionTitle>
                  <div style={{
                    fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                    background: 'rgba(0,194,255,0.08)', border: '1px solid rgba(0,194,255,0.25)', color: '#00C2FF'
                  }}>
                    DEPLOYMENT: globldataprod
                  </div>
                </div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: '#64748B', marginTop: 6 }}>
                  Workflow ID: {deliveryDetail.workflow_id} · Status: <span style={{ color: '#E2E8F0' }}>{deliveryDetail.delivery_status?.toUpperCase() || 'UNKNOWN'}</span>
                </div>
              </div>

              {/* Status flags */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ padding: '6px 14px', borderRadius: 6, background: deliveryDetail.review_readiness?.supervisor_ready ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${deliveryDetail.review_readiness?.supervisor_ready ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, fontSize: 11, fontWeight: 700, color: deliveryDetail.review_readiness?.supervisor_ready ? '#10B981' : '#EF4444' }}>
                  Supervisor review: {deliveryDetail.review_readiness?.supervisor_ready ? 'READY' : 'PENDING'}
                </div>
                <div style={{ padding: '6px 14px', borderRadius: 6, background: deliveryDetail.review_readiness?.client_ready ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${deliveryDetail.review_readiness?.client_ready ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, fontSize: 11, fontWeight: 700, color: deliveryDetail.review_readiness?.client_ready ? '#10B981' : '#EF4444' }}>
                  Client review: {deliveryDetail.review_readiness?.client_ready ? 'READY' : 'PENDING'}
                </div>
              </div>
            </div>

            {/* In-progress rebuild indicator */}
            {revisionWorkflowId && (
              <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, color: '#F59E0B' }}>
                <Spinner size="14px" />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Revision build is currently running and deploying. Live preview will automatically update.</span>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              {[
                ['preview', 'App Live Preview'],
                ['gates', 'Release Gates & QA'],
                ['deliverables', 'Deliverables'],
                ['speckit', 'Architecture Specs']
              ].map(([t, label]) => (
                <div
                  key={t}
                  onClick={() => setActiveTab(t)}
                  style={{
                    padding: '12px 4px', fontSize: 13, fontWeight: 500,
                    color: activeTab === t ? '#00C2FF' : '#94A3B8',
                    borderBottom: activeTab === t ? '2px solid #00C2FF' : '2px solid transparent',
                    cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Tab Contents */}
            <div style={{ flex: 1, minHeight: 0 }}>

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                  {/* Certification Action Center Panel — TOP */}
                  <div style={{ background: '#0A1628', border: '1px solid rgba(0,194,255,0.15)', borderRadius: 12, padding: '24px 32px' }}>
                    <div style={{ fontSize: 12, color: '#00C2FF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                      Handoff &amp; Review Action Center
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {/* Supervisor Approval Button */}
                      {(deliveryDetail.delivery_status === 'ready_for_supervisor_review' || deliveryDetail.review_readiness?.supervisor_ready) && (
                        <button
                          onClick={() => setModalType('supervisor_approval')}
                          disabled={revisionWorkflowId !== null}
                          style={{ padding: '12px 24px', borderRadius: 8, background: 'linear-gradient(135deg, #10B981, #059669)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          🛡️ Approve for Client Review
                        </button>
                      )}

                      {/* Client Approval Button */}
                      {(deliveryDetail.delivery_status === 'ready_for_client_review' || deliveryDetail.review_readiness?.client_ready) && (
                        <button
                          onClick={() => setModalType('client_approval')}
                          disabled={revisionWorkflowId !== null}
                          style={{ padding: '12px 24px', borderRadius: 8, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          🚀 Client Approve Preview
                        </button>
                      )}

                      {/* Regenerate with Feedback Button */}
                      <button
                        onClick={() => setModalType('supervisor_regenerate')}
                        disabled={revisionWorkflowId !== null}
                        style={{ padding: '12px 24px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.25)', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        🔁 Reject &amp; Request Rebuild
                      </button>

                      {/* SQLite QA refresh trigger */}
                      <button
                        onClick={handleRefreshQA}
                        disabled={loadingRefreshQA || revisionWorkflowId !== null}
                        style={{ padding: '12px 24px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, fontSize: 14, cursor: loadingRefreshQA ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        {loadingRefreshQA && <Spinner />}
                        ⚙️ Refresh QA Evidence
                      </button>

                      {/* Mark Ready trigger (Handoff) */}
                      {!deliveryDetail.review_readiness?.supervisor_ready && (
                        <button
                          onClick={handleHandoff}
                          disabled={loadingHandoff || revisionWorkflowId !== null}
                          style={{ padding: '12px 24px', borderRadius: 8, background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', fontWeight: 600, fontSize: 14, cursor: loadingHandoff ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          {loadingHandoff && <Spinner />}
                          🛡️ Handoff to Supervisor
                        </button>
                      )}

                      {/* Redeploy button */}
                      <button
                        onClick={handleDeploy}
                        disabled={loadingDeploy || revisionWorkflowId !== null}
                        style={{ padding: '12px 24px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)', fontWeight: 600, fontSize: 14, cursor: loadingDeploy ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        {loadingDeploy && <Spinner />}
                        🚀 Deploy / Redeploy App
                      </button>
                    </div>
                  </div>

                  {/* Browser App Preview — BELOW */}
                  <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', height: 600, display: 'flex', flexDirection: 'column' }}>
                    {/* Top Browser Bar */}
                    <div style={{ background: '#0A1628', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444' }} />
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B' }} />
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981' }} />
                      </div>
                      <div style={{ flex: 1, background: '#060D1A', borderRadius: 6, border: '1px solid rgba(255,255,255,0.05)', padding: '6px 12px', color: '#94A3B8', fontSize: 11, fontFamily: 'DM Mono', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{deliveryDetail.live_app_url || 'https://globldataprod.s3.amazonaws.com/deploying...'}</span>
                        {deliveryDetail.live_app_url && <a href={deliveryDetail.live_app_url} target="_blank" rel="noreferrer" style={{ color: '#00C2FF', textDecoration: 'none' }}>Open ↗</a>}
                      </div>
                    </div>

                    {/* IFrame App Preview */}
                    <div style={{ flex: 1, background: '#fff' }}>
                      {deliveryDetail.live_app_url ? (
                        <iframe
                          src={deliveryDetail.live_app_url}
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          title="Functional App Preview"
                        />
                      ) : (
                        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#64748B', fontSize: 13 }}>
                          Deployment is in progress or url is currently unavailable.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Gates Tab */}
              {activeTab === 'gates' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
                    {/* QA Evidence Card */}
                    <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 24 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>CRUD QA EVIDENCE</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                          background: deliveryDetail.qa_evidence?.status === 'pass' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: deliveryDetail.qa_evidence?.status === 'pass' ? '#10B981' : '#EF4444'
                        }}>
                          {deliveryDetail.qa_evidence?.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: '#E2E8F0', lineHeight: 1.5 }}>
                        {deliveryDetail.qa_evidence?.status === 'pass'
                          ? 'Real SQLite CRUD evidence successfully executed and certified.'
                          : 'Automated test suite execution pending or contains failing constraints.'}
                      </div>
                    </div>

                    {/* Release Gates Card */}
                    <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 24 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>RELEASE GATES</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                          background: deliveryDetail.latest_deployment?.release_gates?.status === 'pass' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: deliveryDetail.latest_deployment?.release_gates?.status === 'pass' ? '#10B981' : '#EF4444'
                        }}>
                          {deliveryDetail.latest_deployment?.release_gates?.status?.toUpperCase() || 'FAIL'}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: '#E2E8F0', lineHeight: 1.5 }}>
                        SDLC requirements validation check: {deliveryDetail.latest_deployment?.release_gates?.status === 'pass' ? 'PASS' : 'FAIL'}.
                      </div>
                    </div>
                  </div>

                  {/* Blocking reasons block */}
                  {deliveryDetail.review_readiness?.blocking_reasons?.length > 0 && (
                    <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '20px 24px' }}>
                      <div style={{ fontSize: 12, color: '#EF4444', fontWeight: 700, textTransform: 'uppercase', marginBottom: 12 }}>
                        Review Readiness Blockers
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#CBD5E1', fontSize: 13, lineHeight: 1.6 }}>
                        {deliveryDetail.review_readiness.blocking_reasons.map((reason, idx) => (
                          <li key={idx} style={{ marginBottom: 6 }}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Deliverables Tab */}
              {activeTab === 'deliverables' && (
                <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 32 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Available Deliverable Artifacts</div>
                  <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 28 }}>Securely generated assets ready for handoff and production integration.</div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    {/* Markdown Spec */}
                    <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 160 }}>
                      <div>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Spec Markdown</div>
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Full spec breakdown (MD)</div>
                      </div>
                      <button
                        onClick={() => triggerDownload('md', specMdUrl)}
                        disabled={loadingDownloads.md}
                        style={{ width: '100%', padding: '8px', background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: loadingDownloads.md ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {loadingDownloads.md ? <Spinner size="10px" /> : '📥 Download Spec (.md)'}
                      </button>
                    </div>

                    {/* Spec JSON */}
                    <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 160 }}>
                      <div>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>📦</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Spec JSON</div>
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Machine-readable manifest (JSON)</div>
                      </div>
                      <button
                        onClick={() => triggerDownload('json', specJsonUrl)}
                        disabled={loadingDownloads.json}
                        style={{ width: '100%', padding: '8px', background: 'rgba(123,47,255,0.1)', color: '#7B2FFF', border: '1px solid rgba(123,47,255,0.2)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: loadingDownloads.json ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {loadingDownloads.json ? <Spinner size="10px" /> : '📥 Download Spec (.json)'}
                      </button>
                    </div>

                    {/* Code ZIP */}
                    <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 160 }}>
                      <div>
                        <div style={{ fontSize: 24, marginBottom: 8 }}>⚡</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Generated Code Package</div>
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>Full project code repository (ZIP)</div>
                      </div>
                      <button
                        onClick={() => triggerDownload('zip', codeZipUrl)}
                        disabled={loadingDownloads.zip}
                        style={{ width: '100%', padding: '8px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: loadingDownloads.zip ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {loadingDownloads.zip ? <Spinner size="10px" /> : '📥 Download Code (.zip)'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Architecture specs Tab */}
              {activeTab === 'speckit' && (
                <SupervisorSpecKitViewer workflowId={selectedWorkflowId} />
              )}

            </div>
          </>
        )}
      </div>
    </Page>
  );
}

// ─── Spec Kit Viewer (Integrated into Supervisor) ──────────────────────────────
function SupervisorSpecKitViewer({ workflowId, artifactsEndpoint = null, validationEndpoint = null }) {
  const [data, setData] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("spec_md");
  const [available, setAvailable] = useState(false);

  const getAvailableTabs = (arts) => {
    if (!arts) return [];
    const tabs = [];
    const keys = ["spec_md", "plan_md", "research_md", "data_model_md", "constitution_md", "test_md", "tests_md"];
    keys.forEach(k => { if (arts[k]) tabs.push(k); });
    if (arts.tasks && Object.keys(arts.tasks).length > 0) tabs.push("tasks");
    return tabs;
  };

  const sanitizeAndFilter = (text) => {
    if (typeof text !== 'string') return text;
    // Hide local filesystem paths
    return text.replace(/[a-zA-Z]:\\[^ \n\r\t]+/g, '[local-path]')
      .replace(/\/Users\/[^ \n\r\t]+/g, '[local-path]')
      .replace(/\/home\/[^ \n\r\t]+/g, '[local-path]');
  };

  useEffect(() => {
    if (!workflowId) return;

    const fetchData = async () => {
      try {
        const [specRes, valRes] = await Promise.all([
          agenticApi.getSpecKitForWorkflow(workflowId, artifactsEndpoint),
          agenticApi.getSpecKitValidationForWorkflow(workflowId, validationEndpoint)
        ]);

        setData(specRes);
        setValidation(valRes);
        setAvailable(specRes.available);

        if (specRes.available && specRes.selected_artifacts?.artifacts) {
          const tabs = getAvailableTabs(specRes.selected_artifacts.artifacts);
          if (tabs.length > 0 && activeTab === "spec_md" && !tabs.includes("spec_md")) {
            setActiveTab(tabs[0]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch Spec Kit data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Poll if not yet available
    const pollInterval = setInterval(() => {
      if (!available) fetchData();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [workflowId, available]);

  const artifacts = data?.selected_artifacts?.artifacts;
  const availableTabs = getAvailableTabs(artifacts);

  if (loading && !data) return <div style={{ color: '#94A3B8', padding: '20px' }}>Initializing Spec Kit lookup...</div>;

  if (!available) {
    return (
      <div style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
        <SectionTitle style={{ fontSize: 20, color: '#F59E0B' }}>Spec Kit Pending</SectionTitle>
        <SectionSub style={{ marginBottom: 0 }}>
          {data?.message || "Outputs are currently being generated for this workflow. This page will refresh automatically."}
        </SectionSub>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 20 }}>
        <div style={{ flex: 1 }}>
          <SectionTitle style={{ fontSize: 24, marginBottom: 8 }}>Generated Spec Kit</SectionTitle>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {/* ── Spec Download Buttons ── */}
            <a
              href={agenticApi.downloadSpecUrl(workflowId, 'md')}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.25)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
            >
              📥 Download Spec (.md)
            </a>
            <a
              href={agenticApi.downloadSpecUrl(workflowId, 'json')}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: 'rgba(123,47,255,0.1)', color: '#9B5FFF', border: '1px solid rgba(123,47,255,0.25)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
            >
              📥 Download Spec (.json)
            </a>
            {data?.speckit_s3_manifest_url && (
              <a href={data.speckit_s3_manifest_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: 'rgba(0,194,255,0.1)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.2)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                📦 S3 Manifest
              </a>
            )}
            {(data?.speckit_github_paths || []).map((gp, i) => (
              <a key={i} href={gp.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                📁 {gp.path.split('/').pop()}
              </a>
            ))}
          </div>
        </div>

        {validation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderRadius: 10, background: validation.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${validation.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
            <div style={{ fontSize: 20 }}>{validation.ok ? '✅' : '⚠️'}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: validation.ok ? '#10B981' : '#EF4444' }}>
                {validation.ok ? 'Validation Passed' : 'Validation Issues'}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>
                {validation.task_count} Tasks · {validation.validation_errors || 0} Errors
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 24, overflowX: 'auto', paddingBottom: '2px' }}>
        {availableTabs.map(t => (
          <div key={t} onClick={() => setActiveTab(t)} style={{ padding: '10px 4px', fontSize: 13, color: activeTab === t ? '#00C2FF' : '#94A3B8', borderBottom: activeTab === t ? '2px solid #00C2FF' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
            {t.replace('_md', '').replace('_', ' ')}
          </div>
        ))}
      </div>

      {/* Content Rendering */}
      <div style={{ background: '#060D1A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 24px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'DM Mono', fontSize: 12, color: '#00C2FF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {activeTab === 'tasks' ? 'Development Task List' : `${activeTab.replace('_md', '').replace('_', ' ')} Output`}
          </div>
          <div style={{ fontSize: 10, color: '#64748B' }}>UTF-8 · Markdown</div>
        </div>

        <div style={{ padding: '32px', maxHeight: '700px', overflowY: 'auto' }}>
          {activeTab === 'tasks' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {Object.entries(artifacts.tasks || {}).map(([taskName, taskContent]) => (
                <div key={taskName}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} />
                    <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 600, color: '#10B981' }}>{taskName}</div>
                  </div>
                  <pre style={{ background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', fontSize: 13, lineHeight: 1.6, color: '#CBD5E1', whiteSpace: 'pre-wrap', fontFamily: 'DM Mono' }}>
                    {sanitizeAndFilter(taskContent)}
                  </pre>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: 15, lineHeight: 1.8, color: '#F1F5F9' }}>
              {sanitizeAndFilter(artifacts?.[activeTab]) || 'No content available for this section.'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Screen: My PRDs ──────────────────────────────────────────────────────────
function MyPRDsScreen({ onEnhance }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchPRDs = async () => {
      try {
        const res = await agenticApi.getDashboard(100);
        setData(res);
      } catch (err) {
        console.error("Failed to fetch PRDs", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPRDs();
  }, []);

  const workflows = data?.recent_workflows || [];

  const filtered = workflows.filter(w => {
    const name = w.workflow?.workflow_name || '';
    if (search && !name.toLowerCase().includes(search.toLowerCase())) return false;

    const status = w.workflow?.current_status ? String(w.workflow.current_status).toLowerCase().replace(/\s+/g, '_') : '';
    if (filter !== 'all' && status !== filter) return false;

    return true;
  });

  // Compute counts per status from workflow data
  const statusCounts = {};
  workflows.forEach(w => {
    const s = w.workflow?.current_status ? String(w.workflow.current_status).toLowerCase().replace(/\s+/g, '_') : 'unknown';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });

  const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'approved', label: 'Approved' },
    { value: 'awaiting_approval', label: 'Awaiting Approval' },
    { value: 'deploying', label: 'Deploying' },
    { value: 'failed', label: 'Failed' },
    { value: 'features_submitted', label: 'Features Submitted' },
    { value: 'in_review', label: 'In Review' },
    { value: 'ready_for_feature_selection', label: 'Ready for Feature Selection' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <Page style={{ padding: '40px', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <SectionTitle>My PRDs</SectionTitle>
        <SectionSub>View, manage, and enhance your submitted product requirement documents.</SectionSub>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <input
          placeholder="Search PRDs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', color: '#fff', fontSize: 14 }}
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: 240, background: '#0F2040', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px 16px', color: '#fff', fontSize: 14 }}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}{opt.value !== 'all' && statusCounts[opt.value] ? ` (${statusCounts[opt.value]})` : ''}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#94A3B8' }}>Loading PRDs...</div>
      ) : (
        <div style={{ background: '#0A1628', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
          {filtered.length > 0 ? filtered.map((w, i) => (
            <div key={i} style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(0,194,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginRight: 16 }}>📄</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{w.workflow?.workflow_name || 'Untitled PRD'}</div>
                <div style={{ fontFamily: 'DM Mono', fontSize: 12, color: '#64748B' }}>{w.workflow?.prd_type || 'Web Application'} · Last updated {new Date(w.workflow?.updated_at || Date.now()).toLocaleDateString()}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, background: 'rgba(100,116,139,0.1)', color: '#94A3B8', textTransform: 'uppercase' }}>
                  {w.workflow?.current_status || 'UNKNOWN'}
                </span>
                <button
                  onClick={() => onEnhance && onEnhance(w)}
                  style={{ padding: '8px 16px', borderRadius: 8, background: 'linear-gradient(135deg, #00C2FF, #7B2FFF)', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                >
                  Enhance ⚡
                </button>
              </div>
            </div>
          )) : (
            <div style={{ padding: '60px 40px', textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 8 }}>No PRDs Found</div>
              <div style={{ fontSize: 14, color: '#94A3B8' }}>Try adjusting your filters or search terms.</div>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}

// ─── Main Workflow Switcher ──────────────────────────────────────────────────
export default function Workflow({ collapsed, expanded }) {
  const [screen, setScreen] = useState('dashboard');
  const screens = [
    { id: "dashboard", label: "Dashboard" },
    { id: "myprds", label: "My PRDs" },
    { id: "landing", label: "Build" },
    { id: "submit", label: "Submit without PRD" },
    { id: "prd", label: "PRD Upload" },
    { id: "supervisor", label: "Supervisor" },
  ];

  const [activeAppContext, setActiveAppContext] = useState(null);

  const handleEnhance = (workflowData) => {
    setActiveAppContext({
      application_id: workflowData.workflow?.application_id,
      workflow_id: workflowData.workflow?.workflow_id,
      deployment_url: workflowData.live_app_url || workflowData.deployment_url,
      github_repo_url: workflowData.github_repo_url,
      prd_title: `${workflowData.workflow?.workflow_name || 'App'} - Enhancements`
    });
    setScreen('prd');
  };

  const handleWorkflowTransition = (result) => {
    // When a child workflow is created, update the active context to the new workflow
    setActiveAppContext({
      application_id: result.application_id,
      workflow_id: result.workflow_id,
      deployment_url: result.live_app_url || result.deployment_url,
      github_repo_url: result.github_repo_url || result.generated_app_repo_url,
      prd_title: result.prd_title,
      existing_app_s3_object_key: result.existing_app_s3_object_key,
      existing_app_commit_sha: result.existing_app_commit_sha
    });
  };

  const renderContent = () => {
    switch (screen) {
      case 'landing': return <LandingScreen onStart={() => setScreen('prd')} />;
      case 'myprds': return <MyPRDsScreen onEnhance={handleEnhance} />;
      case 'prd': return <PRDUploadScreen initialContext={activeAppContext} onComplete={handleWorkflowTransition} onReturn={() => setScreen('dashboard')} setScreen={setScreen} />;
      case 'dashboard': return <ClientDashboard onEnhance={handleEnhance} onNewRequest={() => setScreen('landing')} />;
      case 'submit': return <SubmitRequest setScreen={setScreen} />;
      case 'supervisor': return <SupervisorWorkspace initialContext={activeAppContext} onAction={(action, data) => {
        if (action === 'enhance') handleEnhance(data);
        else if (action === 'revision') {
          // Switch active context to revision workflow IDs
          setActiveAppContext(prev => ({ ...(prev || {}), workflow_id: data.workflow_id, task_id: data.task_id }));
        }
      }} />;
      default: return <ClientDashboard onEnhance={handleEnhance} onNewRequest={() => setScreen('landing')} />;
    }
  };

  return (
    <WorkflowWrapper collapsed={collapsed} expanded={expanded}>
      {renderContent()}
      <ScreenNavContainer collapsed={collapsed} expanded={expanded}>
        {screens.map(s => (
          <button
            key={s.id}
            onClick={() => setScreen(s.id)}
            style={{ padding: '8px 20px', borderRadius: 32, fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s', border: 'none', background: screen === s.id ? 'linear-gradient(135deg, #00C2FF, #7B2FFF)' : 'none', color: screen === s.id ? '#fff' : '#94A3B8' }}
          >
            {s.label}
          </button>
        ))}
      </ScreenNavContainer>
    </WorkflowWrapper>
  );
}
